/**
 * UNFCCC Reconciliation & Reporting Workspace
 *
 * This is an audit and exception-detection workspace — NOT a cosmetic dashboard.
 * Every ITMO must be traceable, reconcilable, and defensible.
 *
 * Sections:
 *   A. Reconciliation Summary    — portfolio KPIs + CA status breakdown
 *   B. Project Reconciliation    — per-project table with integrity flags
 *   C. CA Status Workflow        — advance CA status with references
 *   D. Bilateral Agreement Registry
 *   E. Export Layer              — BTR JSON / CSV
 */
import React, { useState, useEffect, useCallback } from "react";
import client from "@/api/client";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Download,
  FileText, Globe, RefreshCw, X, Info, Plus, Shield, Check,
  ArrowRight, ExternalLink, AlertCircle, Zap, Clock, CheckCheck,
  XCircle, Eye, Lock, Unlock, History, ScanLine
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
// ── Status helpers ────────────────────────────────────────────────────────
const CA_STATUS_ORDER = ["internal", "submitted", "acknowledged", "reconciled"];
const CA_STATUS_CFG = {
  internal:     { label: "Internal",     color: "bg-slate-100 text-slate-600 border-slate-200" },
  submitted:    { label: "Submitted",    color: "bg-blue-50 text-blue-700 border-blue-200"    },
  acknowledged: { label: "Acknowledged", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  reconciled:   { label: "Reconciled",   color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  not_reported: { label: "Unsubmitted",  color: "bg-amber-50 text-amber-700 border-amber-200" },
};
const normalizeStatus = s => (!s || s === "not_reported" || s === "pending") ? "internal" : s;
const fmtCA = (s) => CA_STATUS_CFG[s] || CA_STATUS_CFG.internal;

const FLAG_SEV = {
  critical: "bg-red-50 border-red-200 text-red-700",
  warning:  "bg-amber-50 border-amber-200 text-amber-700",
  info:     "bg-slate-50 border-slate-200 text-slate-600",
};
const FLAG_ICON = { critical: X, warning: AlertTriangle, info: Info };

function FlagBadge({ flag }) {
  const Icon = FLAG_ICON[flag.severity] || Info;
  const cls  = FLAG_SEV[flag.severity] || FLAG_SEV.info;
  return (
    <div className={`flex items-start gap-1.5 text-xs px-2 py-1 rounded border ${cls}`}>
      <Icon size={10} className="mt-0.5 flex-shrink-0" />
      <span>{flag.message}</span>
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = fmtCA(status);
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

// ── A. Reconciliation Summary ─────────────────────────────────────────────
// ── Governance Scheduler Status Panel ────────────────────────────────────
// ── External Reconciliation Summary ─────────────────────────────────────
function ExternalReconciliationSummary() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    client.get("/cai/erl/reconciliation-summary").then(res => setSummary(res.data)).catch(() => {});
  }, []);

  if (!summary) return null;
  const hasDiscrepancies = summary.mismatches > 0;

  return (
    <div className={`bg-white border rounded-xl p-4 ${hasDiscrepancies ? "border-red-200" : "border-slate-200"}`}
      data-testid="external-reconciliation-summary">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs font-semibold text-slate-900">External Registry Reconciliation</p>
          <p className="text-xs text-slate-400">
            {summary.itmos_reconciled} of {summary.total_itmos} ITMOs reconciled ({summary.itmo_coverage_pct}% coverage) · Manual-entry, API-ready
          </p>
        </div>
        <div className="flex items-center gap-2">
          {summary.fully_matched > 0 && <span className="text-xs text-emerald-600 font-semibold">{summary.fully_matched} matched</span>}
          {summary.mismatches > 0 && <span className="text-xs text-red-600 font-semibold">{summary.mismatches} mismatch</span>}
        </div>
      </div>
      {summary.critical_discrepancies?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <p className="text-xs text-red-700 font-semibold mb-1">Critical Discrepancies ({summary.critical_discrepancies.length})</p>
          {summary.critical_discrepancies.slice(0, 3).map((d, i) => (
            <p key={i} className="text-xs text-red-600">
              {d.entity_id} / {d.field}: internal={d.internal_value} ≠ external={d.external_value}
            </p>
          ))}
        </div>
      )}
      {summary.itmo_coverage_pct === 0 && (
        <p className="text-xs text-slate-400 italic mt-1">
          No ITMOs have been reconciled against external registries yet. Expand an ITMO record in Project Registry to submit external data.
        </p>
      )}
    </div>
  );
}

function SchedulerStatusPanel() {
  const [status,   setStatus]   = useState(null);
  const [running,  setRunning]  = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await client.get("/cai/scheduler/status");
      setStatus(res.data);
    } catch {}
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleRunAll = async () => {
    setRunning(true);
    try {
      const res = await client.post("/cai/scheduler/run-all");
      const total = Object.values(res.data.results || {}).reduce((a, r) => a + (r.flags_created || 0), 0);
      toast.success(`Governance scan complete — ${total} flag(s) created`);
      loadStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Run failed"); }
    setRunning(false);
  };

  const handleRunWatch = async () => {
    setRunning(true);
    try {
      const res = await client.post("/cai/scheduler/watch-cycle");
      toast.success(`Watch cycle complete — ${res.data.projects_scanned} project(s) scanned`);
      loadStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Watch failed"); }
    setRunning(false);
  };

  if (!status) return null;

  const lastRuns = (status.jobs || []).map(j => j.last_run_at).filter(Boolean).sort().reverse();
  const lastRun  = lastRuns[0];
  const nextRuns = (status.jobs || []).map(j => j.next_run_at).filter(Boolean).sort();
  const nextRun  = nextRuns[0];
  const hasWatches = (status.active_watch_count || 0) > 0 || (status.cooldown_watch_count || 0) > 0;

  return (
    <div className={`bg-white border rounded-xl overflow-hidden ${hasWatches ? "border-orange-200" : "border-slate-200"}`}
      data-testid="scheduler-status-panel">
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50"
        onClick={() => setExpanded(e => !e)}>
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${hasWatches ? "bg-orange-400 animate-pulse" : "bg-emerald-400 animate-pulse"}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold text-slate-900">Governance Scheduler</p>
            {hasWatches && (
              <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200"
                data-testid="active-watch-badge">
                {(status.active_watch_count || 0)} Active · {(status.cooldown_watch_count || 0)} Cooldown
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400">
            {lastRun ? `Last: ${new Date(lastRun).toLocaleString()}` : "No runs yet"}
            {nextRun && ` · Next: ${new Date(nextRun).toLocaleString()}`}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {hasWatches && (
            <button onClick={e => { e.stopPropagation(); handleRunWatch(); }} disabled={running}
              className="flex items-center gap-1 px-2 h-7 text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 disabled:opacity-50"
              data-testid="run-watch-cycle-btn">
              <ScanLine size={9} className={running ? "animate-pulse" : ""} />
              Watch
            </button>
          )}
          <button onClick={e => { e.stopPropagation(); handleRunAll(); }} disabled={running}
            className="flex items-center gap-1 px-2 h-7 text-xs font-semibold bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 disabled:opacity-50 flex-shrink-0"
            data-testid="run-all-jobs-btn">
            <ScanLine size={10} className={running ? "animate-pulse" : ""} />
            {running ? "..." : "Run All"}
          </button>
        </div>
        {expanded ? <ChevronDown size={13} className="text-slate-400" /> : <ChevronRight size={13} className="text-slate-400" />}
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-4 pb-4 pt-3 space-y-3 bg-slate-50/30">
          {/* Watch monitoring (active + cooldown) */}
          {(status.active_watch_count > 0 || status.cooldown_watch_count > 0) && (
            <div data-testid="active-watches-section">
              <p className="text-xs text-slate-400 font-semibold uppercase mb-1.5">Watch Monitoring</p>
              <div className="space-y-1">
                {(status.active_watches || []).map(w => {
                  const isCooldown = w.watch_status === "cooldown";
                  return (
                    <div key={w.project_id}
                      className={`flex items-center gap-2 text-xs rounded-lg px-3 py-1.5 border ${
                        isCooldown ? "bg-amber-50 border-amber-200" : "bg-orange-50 border-orange-200"
                      }`}
                      data-testid={`watch-row-${w.project_id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        isCooldown ? "bg-amber-400" : "bg-orange-400 animate-pulse"
                      }`} />
                      <span className={`font-mono font-semibold ${isCooldown ? "text-amber-700" : "text-orange-700"}`}>{w.project_id}</span>
                      {isCooldown ? (
                        <span className="text-amber-600 text-xs">
                          Cooldown: {w.cooldown_cycles_remaining} cycle{w.cooldown_cycles_remaining !== 1 ? "s" : ""} remaining
                        </span>
                      ) : (
                        <span className="text-orange-600 text-xs">{w.critical_flag_count} critical</span>
                      )}
                      <span className="ml-auto text-xs text-slate-400">
                        {w.next_watch_at ? `next: ${new Date(w.next_watch_at).toLocaleTimeString()}` : "pending"}
                      </span>
                      <span className="text-xs text-slate-400">{w.scans_run}×</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Job rows */}
          <div>
            <p className="text-xs text-slate-400 font-semibold uppercase mb-1.5">Scheduled Jobs (24h)</p>
            {(status.jobs || []).map(job => (
              <div key={job.job_id} className="flex items-center gap-3 text-xs py-1.5 border-b border-slate-100 last:border-0"
                data-testid={`scheduler-job-${job.job_id}`}>
                <span className="font-mono text-xs text-slate-400 w-14 flex-shrink-0">{job.job_id}</span>
                <span className="text-slate-600 flex-1 truncate">{job.name}</span>
                <span className="text-xs text-slate-400">{job.threshold_days}d</span>
                {job.last_run_result && (
                  <span className="text-xs text-slate-400">{job.last_run_result.flags_created ?? 0}+</span>
                )}
                <span className={`text-xs ${job.last_run_at ? "text-emerald-500" : "text-slate-300"}`}>
                  {job.last_run_at ? new Date(job.last_run_at).toLocaleDateString() : "Never"}
                </span>
              </div>
            ))}
          </div>

          <p className="text-xs text-slate-300 italic">
            {status.interval_hours}h schedule · {status.watch_interval_hours}h watch · cooldown {status.watch_cooldown_cycles} cycles · event-driven active
          </p>
        </div>
      )}
    </div>
  );
}

function ReconciliationSummary({ portfolio, onRefresh, loading }) {
  const ps = portfolio?.portfolio_summary || {};
  const caStatus = portfolio?.ca_by_status || {};

  const caTotal = Object.values(caStatus).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-5" data-testid="reconciliation-summary">
      {/* Header KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Projects",    value: portfolio?.total_projects ?? "—",   sub: `${portfolio?.authorized_projects ?? 0} authorized`,   color: "text-slate-900" },
          { label: "Total ITMO Volume", value: ps.total_itmos_tco2e != null ? `${ps.total_itmos_tco2e.toLocaleString()} t` : "—", sub: "tCO₂e recorded", color: "text-teal-600" },
          { label: "Integrity Flags",   value: ps.total_flags ?? "—",             sub: `${ps.critical_flags ?? 0} critical`,   color: ps.critical_flags > 0 ? "text-red-500" : "text-emerald-600" },
          { label: "Verified Reductions", value: ps.total_reduction_tco2e != null ? `${ps.total_reduction_tco2e.toLocaleString()} t` : "—", sub: "tCO₂e baseline", color: "text-blue-600" },
        ].map(({ label, value, sub, color }) => (
          <MetricCard key={label} label={label} value={value} sub={sub} color={color} testid={`kpi-${label.toLowerCase().replace(/\s+/g,"-")}`} />
        ))}
      </div>

      {/* CA Status Breakdown */}
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="ca-status-breakdown">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Corresponding Adjustment (CA) Status Breakdown</h3>
            <p className="text-xs text-slate-400 mt-0.5">{caTotal} total CA records across all projects</p>
          </div>
          <button onClick={onRefresh} disabled={loading} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700">
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {caTotal === 0 ? (
          <div className="text-center py-6 text-xs text-slate-400">
            No CA records yet. ITMOs must be recorded before CA tracking can begin.
          </div>
        ) : (
          <div className="space-y-3">
            {CA_STATUS_ORDER.map(s => {
              const count = caStatus[s] || 0;
              const legacy = s === "internal" ? (caStatus.not_reported || 0) : 0;
              const total  = count + legacy;
              const pct    = caTotal > 0 ? (total / caTotal) * 100 : 0;
              const cfg    = fmtCA(s);
              return (
                <div key={s} data-testid={`ca-status-row-${s}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <StatusPill status={s} />
                      <span className="text-xs text-slate-500">{CA_STATUS_CFG[s]?.label || s}</span>
                    </div>
                    <span className="text-xs font-bold text-slate-900">{total}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-[#059669] transition-all duration-500"
                      style={{ width: `${pct}%`, backgroundColor: s === "reconciled" ? "#059669" : s === "acknowledged" ? "#6366F1" : s === "submitted" ? "#3B82F6" : "#94A3B8" }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Coverage ratio */}
        {portfolio && (
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-4 text-center">
            {[
              { label: "ITMO Coverage", value: ps.total_reduction_tco2e > 0 ? `${((ps.total_itmos_tco2e / ps.total_reduction_tco2e) * 100).toFixed(1)}%` : "—", note: "of verified reductions used" },
              { label: "Remaining Headroom", value: ps.total_reduction_tco2e > 0 ? `${(ps.total_reduction_tco2e - ps.total_itmos_tco2e).toFixed(1)} t` : "—", note: "available for ITMO issuance" },
              { label: "Projects with ITMOs", value: portfolio.projects?.filter(p => p.itmo_count > 0).length ?? "—", note: `of ${portfolio.total_projects || 0} total` },
            ].map(({ label, value, note }) => (
              <div key={label}>
                <p className="text-xs text-slate-400">{label}</p>
                <p className="text-sm font-semibold text-slate-900 mt-0.5">{value}</p>
                <p className="text-xs text-slate-400">{note}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Integrity Alert Banner */}
      {ps.critical_flags > 0 && (
        <div className="border-2 border-red-300 bg-red-50 rounded-xl p-4 flex items-start gap-3" data-testid="critical-flag-banner">
          <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-red-700">Critical Integrity Violations Detected</p>
            <p className="text-xs text-red-600 mt-0.5">
              {ps.critical_flags} critical flag(s) found across the portfolio. ITMO volumes may exceed verified reductions.
              Review the Project Reconciliation tab immediately to resolve before BTR submission.
            </p>
          </div>
        </div>
      )}

      {/* Governance Scheduler Status — compact summary */}
      <SchedulerStatusPanel />

      {/* External Reconciliation Summary */}
      <ExternalReconciliationSummary />
    </div>
  );
}

// ── B. Project Reconciliation Table ──────────────────────────────────────
function ProjectReconciliation({ projects, onSelectCA }) {
  const [expanded, setExpanded] = useState({});

  if (!projects?.length) return (
    <div className="text-center py-10 text-slate-400 text-sm">No projects found.</div>
  );

  return (
    <div className="space-y-3" data-testid="project-reconciliation-table">
      {/* Column headers */}
      <div className="hidden lg:grid grid-cols-12 gap-2 text-xs text-slate-400 uppercase tracking-wider font-semibold px-4">
        <div className="col-span-3">Project</div>
        <div className="col-span-1">Country</div>
        <div className="col-span-1 text-right">ITMO Vol</div>
        <div className="col-span-1 text-right">Credits</div>
        <div className="col-span-2">CA Status</div>
        <div className="col-span-2">BTR Cycle</div>
        <div className="col-span-2">Integrity</div>
      </div>

      {projects.map(p => {
        const isExp = expanded[p.project_id];
        const integrity = p.volume_integrity;
        const intOK = integrity?.status === "compliant";
        const caRec = p.ca_reconciliation;
        const primaryCA = Object.keys(caRec?.by_status || {}).sort((a, b) =>
          CA_STATUS_ORDER.indexOf(normalizeStatus(b)) - CA_STATUS_ORDER.indexOf(normalizeStatus(a))
        )[0];

        return (
          <div key={p.project_id}
            className="bg-white border border-slate-200 rounded-xl overflow-hidden"
            data-testid={`project-row-${p.project_id}`}>

            {/* Main row */}
            <div
              className="grid grid-cols-12 gap-2 items-center px-4 py-3 cursor-pointer hover:bg-slate-50"
              onClick={() => setExpanded(e => ({ ...e, [p.project_id]: !isExp }))}>
              {/* Project */}
              <div className="col-span-3">
                <div className="flex items-center gap-1.5">
                  {isExp ? <ChevronDown size={12} className="text-slate-400 flex-shrink-0" /> : <ChevronRight size={12} className="text-slate-400 flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-900 truncate">{p.project_name}</p>
                    <p className="text-xs font-mono text-slate-400">{p.project_id}</p>
                  </div>
                </div>
              </div>
              {/* Country */}
              <div className="col-span-1">
                <p className="text-xs text-slate-600">{p.country || "—"}</p>
              </div>
              {/* ITMO Vol */}
              <div className="col-span-1 text-right">
                <p className="text-xs font-bold text-teal-700">{integrity?.itmos_recorded_tco2e ?? 0} t</p>
              </div>
              {/* Credits */}
              <div className="col-span-1 text-right">
                <p className="text-xs text-slate-600">{p.lineage?.credits_issued ?? 0}</p>
              </div>
              {/* CA Status */}
              <div className="col-span-2">
                {primaryCA ? (
                  <StatusPill status={normalizeStatus(primaryCA)} />
                ) : (
                  <span className="text-xs text-slate-400 italic">No records</span>
                )}
              </div>
              {/* BTR Cycle — derived from itmos */}
              <div className="col-span-2">
                <p className="text-xs text-slate-500">
                  {p.itmos?.[0] ? `BTR ${p.itmos[0].vintage_year || "—"}-${(p.itmos[0].vintage_year || 0) + 1}` : "—"}
                </p>
              </div>
              {/* Integrity */}
              <div className="col-span-2">
                <div className="flex items-center gap-1.5">
                  {intOK
                    ? <span className="flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 size={10} /> Compliant</span>
                    : <span className="flex items-center gap-1 text-xs text-red-600"><X size={10} /> Violation</span>
                  }
                  {p.flags?.length > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">
                      {p.flags.length}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Expanded detail */}
            {isExp && (
              <div className="border-t border-slate-100 p-4 space-y-4 bg-slate-50/50">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Volume integrity */}
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-semibold mb-2">Volume Integrity</p>
                    <div className="space-y-1.5">
                      {[
                        ["Verified Reduction", `${integrity?.total_reduction_tco2e ?? 0} tCO₂e`],
                        ["Active Credits",     `${integrity?.credits_active_tco2e ?? 0} tCO₂e`],
                        ["ITMO Recorded",      `${integrity?.itmos_recorded_tco2e ?? 0} tCO₂e`],
                        ["Headroom Left",      `${integrity?.available_tco2e ?? 0} tCO₂e`],
                      ].map(([l, v]) => (
                        <div key={l} className="flex justify-between text-xs">
                          <span className="text-slate-400">{l}</span>
                          <span className="font-semibold text-slate-900">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CA Reconciliation */}
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-semibold mb-2">CA Reconciliation</p>
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">CA Records</span>
                        <span className="font-semibold text-slate-900">{caRec?.total_ca_records ?? 0}</span>
                      </div>
                      {Object.entries(caRec?.by_status || {}).map(([s, c]) => (
                        <div key={s} className="flex justify-between items-center text-xs">
                          <StatusPill status={normalizeStatus(s)} />
                          <span className="font-semibold">{c}</span>
                        </div>
                      ))}
                      {caRec?.total_ca_records > 0 && (
                        <div className="flex justify-between text-xs mt-1">
                          <span className="text-slate-400">Fully reconciled</span>
                          <span className={`font-semibold ${caRec.fully_reconciled ? "text-emerald-600" : "text-amber-600"}`}>
                            {caRec.fully_reconciled ? "Yes" : "No"}
                          </span>
                        </div>
                      )}
                    </div>
                    {p.itmo_count > 0 && !caRec?.fully_reconciled && (
                      <button onClick={() => onSelectCA(p)} data-testid={`advance-ca-btn-${p.project_id}`}
                        className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold">
                        <ArrowRight size={9} /> Advance CA Status
                      </button>
                    )}
                  </div>

                  {/* Authorization */}
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-semibold mb-2">Authorization</p>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-center gap-2">
                        {p.authorization?.is_authorized
                          ? <CheckCircle2 size={12} className="text-emerald-500" />
                          : <X size={12} className="text-red-400" />}
                        <span className="text-slate-500">ITMO Authorized</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {p.authorization?.loa_verified
                          ? <CheckCircle2 size={12} className="text-emerald-500" />
                          : <X size={12} className="text-amber-400" />}
                        <span className="text-slate-500">LoA Verified</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Article 6 Status: </span>
                        <span className="font-medium capitalize">{p.authorization?.article6_status?.replace(/_/g, " ") || "—"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Integrity Flags */}
                {p.flags?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-semibold mb-2">Integrity Flags</p>
                    <div className="space-y-1.5">
                      {p.flags.map((f, i) => <FlagBadge key={i} flag={f} />)}
                    </div>
                  </div>
                )}

                {/* ITMO list */}
                {p.itmos?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-semibold mb-2">ITMO Records + External Reconciliation</p>
                    <div className="space-y-2">
                      {p.itmos.map(it => (
                        <div key={it.itmo_id} className="bg-white rounded-xl px-3 py-2 border border-slate-200">
                          <div className="flex items-center gap-3 text-xs">
                            <span className="font-mono text-teal-700 font-semibold">{it.itmo_id}</span>
                            <span className="text-slate-400">{it.volume} tCO₂e → {it.buyer}</span>
                            <StatusPill status={normalizeStatus(it.status)} />
                            <ERLBadge erl={{ erl_status: it.erl_status, erl_ref_id: it.erl_ref_id, erl_type: it.erl_type }} compact={true} />
                            <span className={`ml-auto text-xs ${it.has_lineage ? "text-emerald-500" : "text-amber-500"}`}>
                              {it.has_lineage ? "Lineage OK" : "No lineage"}
                            </span>
                          </div>
                          {/* External reconciliation inline */}
                          <ExternalReconcilePanel entityType="itmo" entityId={it.itmo_id} onSaved={null} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── C. CA Status Workflow ─────────────────────────────────────────────────
function CAStatusWorkflow({ projects }) {
  const [itmos,     setItmos]     = useState([]);
  const [selected,  setSelected]  = useState("");
  const [form,      setForm]      = useState({ reporting_status: "", unfccc_submission_reference: "", reporting_cycle: "", notes: "" });
  const [loading,   setLoading]   = useState(false);
  const [result,    setResult]    = useState(null);

  // Collect all ITMOs from all projects
  useEffect(() => {
    const allItmos = [];
    (projects || []).forEach(p => {
      (p.itmos || []).forEach(it => allItmos.push({ ...it, project_name: p.project_name, project_id: p.project_id }));
    });
    setItmos(allItmos);
  }, [projects]);

  const selectedItmo = itmos.find(it => it.itmo_id === selected);
  const currentStatusNorm = normalizeStatus(selectedItmo?.status);
  const currentIdx = CA_STATUS_ORDER.indexOf(currentStatusNorm);
  const nextStatuses = CA_STATUS_ORDER.slice(currentIdx + 1);

  const handleAdvance = async () => {
    if (!selected || !form.reporting_status) {
      toast.warning("Select an ITMO and target status.");
      return;
    }
    if (form.reporting_status === "submitted" && !form.unfccc_submission_reference) {
      toast.warning("Submission reference required when advancing to 'submitted'.");
      return;
    }
    setLoading(true);
    try {
      const res = await client.patch(`/cai/reporting/ca/${selected}`, form);
      setResult(res.data);
      toast.success(`CA status advanced: ${res.data.previous_ca_status} → ${res.data.new_ca_status}`);
      setForm({ reporting_status: "", unfccc_submission_reference: "", reporting_cycle: "", notes: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "CA advancement failed");
    }
    setLoading(false);
  };

  return (
    <div className="space-y-5" data-testid="ca-workflow">
      {/* Intro note */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
        <Shield size={16} className="text-slate-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-slate-900">CA Reconciliation Ladder</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Corresponding Adjustment status must advance in order: <strong>Internal → Submitted → Acknowledged → Reconciled</strong>.
            Each advancement requires supporting references and cannot be reversed.
            Only advance status when the corresponding action has been taken with the UNFCCC or national registry.
          </p>
        </div>
      </div>

      {/* Status ladder visual */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-slate-900 mb-3">Status Ladder</p>
        <div className="flex items-center gap-0 overflow-x-auto">
          {CA_STATUS_ORDER.map((s, i) => {
            const cfg = fmtCA(s);
            const norm = normalizeStatus(selectedItmo?.status);
            const isDone    = CA_STATUS_ORDER.indexOf(norm) > i;
            const isCurrent = norm === s;
            return (
              <React.Fragment key={s}>
                {i > 0 && <div className={`h-0.5 flex-1 min-w-4 ${isDone || isCurrent ? "bg-[#059669]" : "bg-slate-200"}`} />}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center border-2 ${isDone ? "bg-emerald-100 border-emerald-400" : isCurrent ? "bg-[#059669] border-[#059669]" : "bg-white border-slate-200"}`}>
                    {isDone ? <Check size={12} className="text-emerald-600" strokeWidth={3} /> : (
                      <span className={`text-xs font-bold ${isCurrent ? "text-white" : "text-slate-400"}`}>{i + 1}</span>
                    )}
                  </div>
                  <p className={`text-xs mt-1 font-semibold ${isCurrent ? "text-[#059669]" : isDone ? "text-slate-500" : "text-slate-300"}`}>
                    {CA_STATUS_CFG[s]?.label || s}
                  </p>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Form */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Advance CA Status</h3>

        <div className="space-y-4">
          {/* ITMO selector */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Select ITMO Transaction</label>
            <select
              value={selected}
              onChange={e => { setSelected(e.target.value); setResult(null); }}
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#059669]"
              data-testid="itmo-select">
              <option value="">Select ITMO...</option>
              {itmos.map(it => (
                <option key={it.itmo_id} value={it.itmo_id}>
                  {it.itmo_id} — {it.project_name} ({it.volume} tCO₂e → {it.buyer}) [{normalizeStatus(it.status)}]
                </option>
              ))}
            </select>
          </div>

          {/* Current status display */}
          {selectedItmo && (
            <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-3">
              <div>
                <p className="text-xs text-slate-400">Current CA Status</p>
                <div className="mt-1"><StatusPill status={currentStatusNorm} /></div>
              </div>
              <ChevronRight size={14} className="text-slate-300" />
              <div>
                <p className="text-xs text-slate-400">Target</p>
                {form.reporting_status
                  ? <div className="mt-1"><StatusPill status={form.reporting_status} /></div>
                  : <p className="text-xs text-slate-300 mt-1">Not selected</p>}
              </div>
            </div>
          )}

          {/* Target status */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Advance To</label>
            <select
              value={form.reporting_status}
              onChange={e => setForm(f => ({ ...f, reporting_status: e.target.value }))}
              disabled={!selected}
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#059669] disabled:opacity-50"
              data-testid="target-status-select">
              <option value="">Select target status...</option>
              {nextStatuses.map(s => (
                <option key={s} value={s}>{CA_STATUS_CFG[s]?.label} — {s}</option>
              ))}
            </select>
          </div>

          {/* Submission reference (required for submitted+) */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">
              UNFCCC Submission Reference
              {form.reporting_status === "submitted" && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              value={form.unfccc_submission_reference}
              onChange={e => setForm(f => ({ ...f, unfccc_submission_reference: e.target.value }))}
              placeholder="e.g. UNFCCC-BTR-2024-NG-0042"
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#059669]"
              data-testid="unfccc-ref-input" />
          </div>

          {/* Reporting cycle */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Reporting Cycle</label>
            <input
              value={form.reporting_cycle}
              onChange={e => setForm(f => ({ ...f, reporting_cycle: e.target.value }))}
              placeholder="e.g. BTR 2023-2024"
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#059669]"
              data-testid="reporting-cycle-input" />
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Add any relevant notes for this status advancement..."
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#059669] resize-none"
              data-testid="ca-notes-input" />
          </div>

          <button
            onClick={handleAdvance}
            disabled={loading || !selected || !form.reporting_status}
            data-testid="advance-ca-submit-btn"
            className="w-full h-10 bg-[#059669] text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-40 flex items-center justify-center gap-2">
            {loading ? <RefreshCw size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            {loading ? "Processing..." : "Advance CA Status"}
          </button>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="ca-advance-result">
          <div className="flex items-start gap-2">
            <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-emerald-700">CA Status Advanced</p>
              <p className="text-xs text-emerald-600 mt-0.5">
                ITMO <strong>{result.itmo_id}</strong>: {result.previous_ca_status} → <strong>{result.new_ca_status}</strong>
              </p>
              {result.unfccc_ref && (
                <p className="text-xs text-emerald-600 mt-0.5">Ref: {result.unfccc_ref}</p>
              )}
              <p className="text-xs text-emerald-600 mt-0.5 italic">{result.ca_status_label}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── D. Bilateral Agreement Registry ──────────────────────────────────────
function BilateralRegistry() {
  const [agreements, setAgreements] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [creating,   setCreating]   = useState(false);
  const [form, setForm] = useState({
    host_country: "", buyer_country: "", agreement_name: "", pathway: "art6_2",
    corresponding_adjustment_rules: "applied_at_issuance", signing_date: "",
    effective_date: "", expiry_date: "", official_reference: "", notes: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/cai/bilateral/");
      setAgreements(res.data.agreements || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.host_country || !form.buyer_country) {
      toast.warning("Host and buyer country required.");
      return;
    }
    setCreating(true);
    try {
      await client.post("/cai/bilateral/", form);
      toast.success("Bilateral agreement registered.");
      setShowForm(false);
      setForm({ host_country: "", buyer_country: "", agreement_name: "", pathway: "art6_2", corresponding_adjustment_rules: "applied_at_issuance", signing_date: "", effective_date: "", expiry_date: "", official_reference: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setCreating(false);
  };

  return (
    <div className="space-y-4" data-testid="bilateral-registry">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Bilateral Agreement Register</h3>
          <p className="text-xs text-slate-400 mt-0.5">Registered Art 6.2 bilateral frameworks between host and buyer countries</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="add-bilateral-btn"
          className="flex items-center gap-1.5 px-3 py-2 bg-[#0F172A] text-white text-xs font-medium rounded-xl hover:bg-slate-700">
          <Plus size={12} /> Register Agreement
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="bilateral-form">
          <p className="text-xs font-semibold text-slate-900">New Bilateral Agreement</p>
          <div className="grid grid-cols-2 gap-4">
            {[
              ["host_country", "Host Country", "NG — Nigeria"],
              ["buyer_country", "Buyer Country", "CH — Switzerland"],
              ["agreement_name", "Agreement Name (optional)", "NG-CH Art 6.2 Cooperation Agreement"],
              ["official_reference", "Official Reference", "NG-CH-A6-2024-001"],
            ].map(([k, l, p]) => (
              <div key={k}>
                <label className="text-xs text-slate-500 mb-0.5 block">{l}</label>
                <input value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                  placeholder={p} className="w-full h-9 px-3 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-[#059669]" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              ["signing_date", "Signing Date"],
              ["effective_date", "Effective Date"],
              ["expiry_date", "Expiry Date"],
            ].map(([k, l]) => (
              <div key={k}>
                <label className="text-xs text-slate-500 mb-0.5 block">{l}</label>
                <input type="date" value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                  className="w-full h-9 px-3 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-[#059669] bg-white" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500 mb-0.5 block">Pathway</label>
              <select value={form.pathway} onChange={e => setForm(f => ({ ...f, pathway: e.target.value }))}
                className="w-full h-9 px-3 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none">
                <option value="art6_2">Article 6.2 — Bilateral cooperative</option>
                <option value="art6_4">Article 6.4 — UNFCCC supervised</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-0.5 block">CA Rules</label>
              <select value={form.corresponding_adjustment_rules} onChange={e => setForm(f => ({ ...f, corresponding_adjustment_rules: e.target.value }))}
                className="w-full h-9 px-3 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none">
                <option value="applied_at_issuance">Applied at issuance</option>
                <option value="applied_at_transfer">Applied at transfer</option>
                <option value="applied_at_retirement">Applied at retirement</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Notes / Conditions</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Any sector restrictions, volume caps, additionality conditions..."
              rows={2} className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none resize-none" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={creating} data-testid="create-bilateral-submit"
              className="flex-1 h-9 bg-[#059669] text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50">
              {creating ? "Registering..." : "Register Agreement"}
            </button>
            <button onClick={() => setShowForm(false)}
              className="px-4 h-9 border border-slate-200 text-slate-600 text-xs rounded-lg hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Agreement list */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2].map(i => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : agreements.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
          <Globe size={28} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-400">No bilateral agreements registered.</p>
          <p className="text-xs text-slate-300 mt-1">Register a framework to link ITMOs to sovereign bilateral agreements.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {agreements.map(ba => (
            <div key={ba.agreement_id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`bilateral-${ba.agreement_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-slate-900">{ba.agreement_name}</p>
                    <span className="text-xs font-mono px-2 py-0.5 bg-slate-100 text-slate-500 rounded">{ba.agreement_id}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${ba.agreement_status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                      {ba.agreement_status}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">{ba.host_country} → {ba.buyer_country}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">{ba.pathway?.replace("_", " ")}</p>
                  <p className="text-xs text-slate-400">CA: {ba.corresponding_adjustment_rules?.replace(/_/g, " ")}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-3 text-xs">
                {[
                  ["Signed", ba.signing_date || "—"],
                  ["Effective", ba.effective_date || "—"],
                  ["Expires", ba.expiry_date || "—"],
                ].map(([l, v]) => (
                  <div key={l}><span className="text-slate-400">{l}:</span> <strong>{v}</strong></div>
                ))}
              </div>
              {ba.notes && (
                <p className="text-xs text-slate-400 mt-2 italic">{ba.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── E. Export Layer ───────────────────────────────────────────────────────
// ── Submission Readiness Certificate Panel ────────────────────────────────
function SRCPanel() {
  const [prereqs,   setPrereqs]   = useState(null);
  const [srcs,      setSrcs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [creating,  setCreating]  = useState(false);
  const [notes,     setNotes]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prereqRes, listRes] = await Promise.allSettled([
        client.get("/cai/src/prerequisites"),
        client.get("/cai/src/"),
      ]);
      if (prereqRes.status === "fulfilled") setPrereqs(prereqRes.value.data);
      if (listRes.status   === "fulfilled") setSrcs(listRes.value.data.certificates || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleIssue = async () => {
    setCreating(true);
    try {
      const res = await client.post("/cai/src/", { notes: notes || null });
      toast.success(`${res.data.certificate_id} — Submission Readiness Certificate issued`);
      setNotes("");
      load();
    } catch (err) {
      const d = err.response?.data;
      if (typeof d === "object" && d.detail?.blockers) {
        toast.error(`Cannot issue SRC: ${d.detail.blockers[0]}`);
      } else {
        toast.error(err.response?.data?.detail || "SRC issuance failed");
      }
    }
    setCreating(false);
  };

  const canIssue = prereqs?.can_issue;
  const activeSRC = srcs.find(s => s.status === "active");

  return (
    <div className={`bg-white border-2 rounded-xl overflow-hidden ${
      activeSRC ? "border-emerald-300" : canIssue ? "border-teal-200" : "border-slate-200"
    }`} data-testid="src-panel">
      {/* Header */}
      <div className={`px-5 py-4 flex items-start justify-between gap-3 ${
        activeSRC ? "bg-emerald-50" : canIssue ? "bg-teal-50" : "bg-slate-50"
      }`}>
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <Shield size={16} className={activeSRC ? "text-emerald-600" : "text-teal-600"} />
            <p className="text-sm font-semibold text-slate-900">Submission Readiness Certificate (SRC)</p>
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 font-bold">
              INSTITUTIONAL OUTPUT
            </span>
          </div>
          <p className="text-xs text-slate-500">
            An SRC is the output of a completed governance cycle — reconciliation, attestation, and audit snapshot.
            It is an internal governance instrument issued by DeCarbonIQ, not a regulatory certification or UNFCCC authorization.
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="text-xs text-slate-400 hover:text-slate-700 flex-shrink-0 mt-0.5">
          <RefreshCw size={10} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* Active SRC display */}
        {activeSRC && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="active-src">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-bold text-emerald-700">{activeSRC.certificate_id}</p>
                  <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">ACTIVE</span>
                </div>
                <p className="text-xs text-emerald-600">
                  Valid until {new Date(activeSRC.valid_until).toLocaleDateString()}
                </p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  {[
                    ["ITMOs", `${activeSRC.readiness?.matched_itmos}/${activeSRC.readiness?.total_itmos} matched`],
                    ["Organization", activeSRC.authority?.attesting_org || "—"],
                    ["Signed by", activeSRC.authority?.signing_authority || "—"],
                    ["Scope", activeSRC.authority?.delegation_scope?.replace(/_/g," ") || "—"],
                  ].map(([l,v]) => (
                    <div key={l}><span className="text-emerald-400">{l}:</span> <strong className="text-emerald-700">{v}</strong></div>
                  ))}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <a href={`/api/public/src/${activeSRC.certificate_id}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-emerald-600 border border-emerald-200 rounded-lg px-2 py-1 hover:bg-emerald-100"
                  data-testid="src-verify-link">
                  <ExternalLink size={9} /> Verify
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Prerequisites checklist */}
        {loading ? (
          <div className="h-12 bg-slate-100 rounded-xl animate-pulse" />
        ) : prereqs && !canIssue ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs text-amber-700 font-semibold mb-1.5">Prerequisites Required</p>
            <div className="space-y-1">
              {prereqs.blockers?.map((b, i) => (
                <div key={i} className="flex items-start gap-1 text-xs text-amber-700">
                  <X size={9} className="flex-shrink-0 mt-0.5" /> {b}
                </div>
              ))}
            </div>
          </div>
        ) : prereqs && canIssue && !activeSRC ? (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 flex items-center gap-2">
            <CheckCircle2 size={13} className="text-teal-600 flex-shrink-0" />
            <p className="text-xs text-teal-700 font-semibold">All prerequisites met — portfolio is submission-ready</p>
          </div>
        ) : null}

        {/* Issue SRC form */}
        {canIssue && !activeSRC && (
          <div className="flex items-end gap-2" data-testid="src-issue-form">
            <div className="flex-1">
              <label className="text-xs text-slate-400 mb-0.5 block">Notes (optional)</label>
              <input value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="e.g. Q1 2026 BTR submission package"
                className="w-full h-9 px-3 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400"
                data-testid="src-notes-input" />
            </div>
            <button onClick={handleIssue} disabled={creating || !canIssue}
              className="flex items-center gap-1.5 px-4 h-9 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 flex-shrink-0"
              data-testid="issue-src-btn">
              <Shield size={12} className={creating ? "animate-pulse" : ""} />
              {creating ? "Issuing..." : "Issue Certificate"}
            </button>
          </div>
        )}

        {/* History */}
        {srcs.length > 1 && (
          <div className="space-y-1">
            <p className="text-xs text-slate-400 font-semibold">Certificate History</p>
            {srcs.filter(s => s.status !== "active").slice(0, 3).map(s => (
              <div key={s.certificate_id} className="flex items-center gap-2 text-xs text-slate-500 border border-slate-100 rounded-lg px-3 py-1.5">
                <span className="font-mono">{s.certificate_id}</span>
                <span className={`px-1 py-0.5 rounded text-xs font-semibold ${s.status === "revoked" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500"}`}>{s.status}</span>
                <span className="ml-auto">{new Date(s.issued_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-slate-400 italic">
          The SRC is a system-internal instrument — not a regulatory compliance certificate or legal instrument.
          Backed by SHA-256 verification hash binding: certificate ID + CPIE snapshot + attestation + issuing org.
        </p>
      </div>
    </div>
  );
}

function ExportLayer() {
  const [exporting,       setExporting]       = useState({ json: false, csv: false });
  const [report,          setReport]          = useState(null);
  const [loadingReport,   setLoadingReport]   = useState(true);
  const [exportingRecon,  setExportingRecon]  = useState({ json: false, csv: false });
  const [creatingSnap,    setCreatingSnap]    = useState(false);
  const [snapshots,       setSnapshots]       = useState([]);
  const [snapNotes,       setSnapNotes]       = useState("");

  const loadReport = useCallback(async () => {
    setLoadingReport(true);
    try {
      const [repRes, snapRes] = await Promise.allSettled([
        client.get("/cai/erl/portfolio-report"),
        client.get("/cai/erl/portfolio-report/snapshots"),
      ]);
      if (repRes.status  === "fulfilled") setReport(repRes.value.data);
      if (snapRes.status === "fulfilled") setSnapshots(snapRes.value.data.snapshots || []);
    } catch {}
    setLoadingReport(false);
  }, []);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleBTRExport = async (fmt) => {
    setExporting(e => ({ ...e, [fmt]: true }));
    try {
      const res = await client.get(`/cai/reporting/export?fmt=${fmt}`, { responseType: "blob" });
      const ext = fmt === "csv" ? "csv" : "json", mime = fmt === "csv" ? "text/csv" : "application/json";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a"); a.href = url;
      a.download = `btr-itmo-export-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
      toast.success(`BTR export downloaded (${fmt.toUpperCase()})`);
    } catch { toast.error("Export failed"); }
    setExporting(e => ({ ...e, [fmt]: false }));
  };

  const handleReconExport = async (fmt) => {
    setExportingRecon(e => ({ ...e, [fmt]: true }));
    try {
      const res = await client.get(`/cai/erl/portfolio-report/export?fmt=${fmt}`, { responseType: "blob" });
      const ext = fmt === "csv" ? "csv" : "json", mime = fmt === "csv" ? "text/csv" : "application/json";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a"); a.href = url;
      const ready = report?.portfolio_submission_ready;
      a.download = `portfolio-recon-${new Date().toISOString().slice(0,10)}-${ready?"READY":"NOT_READY"}.${ext}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
      toast.success(`Portfolio reconciliation export downloaded (${fmt.toUpperCase()})`);
    } catch { toast.error("Export failed"); }
    setExportingRecon(e => ({ ...e, [fmt]: false }));
  };

  const handleCreateSnapshot = async () => {
    setCreatingSnap(true);
    try {
      const res = await client.post("/cai/erl/portfolio-report/snapshot", { notes: snapNotes || null });
      toast.success(`${res.data.snapshot_id} — ${res.data.readiness_label}`);
      setSnapNotes("");
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Snapshot failed"); }
    setCreatingSnap(false);
  };

  const isReady = report?.portfolio_submission_ready;
  const summary = report?.summary || {};

  return (
    <div className="space-y-6" data-testid="export-layer">

      {/* ─── Submission Readiness Certificate (Institutional tier flagship) ─── */}
      <SRCPanel />

      {/* ─── Portfolio Reconciliation Report ─── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="portfolio-recon-report">
        {/* Header */}
        <div className={`px-5 py-4 border-b flex items-start justify-between gap-3 ${
          isReady === false ? "bg-red-50 border-red-200" :
          isReady === true  ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"
        }`}>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              {isReady === true  && <CheckCircle2 size={15} className="text-emerald-600" />}
              {isReady === false && <AlertCircle size={15} className="text-red-500" />}
              <p className={`text-sm font-bold ${isReady === true ? "text-emerald-700" : isReady === false ? "text-red-700" : "text-slate-700"}`}>
                Portfolio Reconciliation Report
                {!loadingReport && report && (
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full border font-semibold ${
                    isReady ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-red-100 text-red-700 border-red-300"
                  }`} data-testid="readiness-label">
                    {report.readiness_label}
                  </span>
                )}
              </p>
            </div>
            <p className="text-xs text-slate-500">
              Field-level external reconciliation status across all portfolio ITMOs. Sorted by criticality.
            </p>
            {report?.not_ready_reasons?.length > 0 && (
              <div className="mt-2 space-y-0.5">
                {report.not_ready_reasons.map((r, i) => (
                  <p key={i} className="text-xs text-red-600 flex items-center gap-1">
                    <X size={8} /> {r}
                  </p>
                ))}
              </div>
            )}
          </div>
          <button onClick={loadReport} disabled={loadingReport}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 flex-shrink-0 mt-0.5">
            <RefreshCw size={10} className={loadingReport ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-100">
          {[
            { label: "Total ITMOs",      value: summary.total_itmos ?? "—",          color: "#0F172A" },
            { label: "Matched",          value: summary.matched ?? "—",              color: "#059669" },
            { label: "Mismatched",       value: summary.mismatched ?? "—",           color: "#DC2626" },
            { label: "Unreconciled",     value: summary.unreconciled ?? "—",         color: "#F59E0B" },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white px-4 py-3 text-center">
              <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
              <p className="text-xl font-bold" style={{ color }}>{value}</p>
            </div>
          ))}
        </div>

        {/* ITMO table */}
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          {loadingReport ? (
            <div className="p-8 text-center text-xs text-slate-400">Loading...</div>
          ) : !report?.itmos?.length ? (
            <div className="p-8 text-center text-xs text-slate-400">No ITMOs found.</div>
          ) : (
            <table className="w-full min-w-[900px] text-xs" data-testid="recon-itmo-table">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr>
                  {["ITMO ID","Project","Host","Buyer","Vol","Vintage","Pathway","ERL","Reconciliation","Severity","Flags","Ready"].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {report.itmos.map((it, i) => {
                  const rowColor = it.reconciliation_status === "mismatch" ? "bg-red-50/40" :
                                   it.reconciliation_status === "partial_match" ? "bg-amber-50/30" :
                                   it.reconciliation_status === "matched" ? "bg-emerald-50/20" : "";
                  return (
                    <tr key={it.itmo_id} className={`hover:bg-slate-50 ${rowColor}`} data-testid={`recon-row-${it.itmo_id}`}>
                      <td className="px-3 py-2 font-mono font-semibold text-teal-700">{it.itmo_id}</td>
                      <td className="px-3 py-2 truncate max-w-[100px]" title={it.project_name}>{it.project_name}</td>
                      <td className="px-3 py-2 text-slate-500">{it.host_country}</td>
                      <td className="px-3 py-2 text-slate-500">{it.buyer_country}</td>
                      <td className="px-3 py-2 font-semibold">{it.volume_tco2e}</td>
                      <td className="px-3 py-2 text-slate-500">{it.vintage_year}</td>
                      <td className="px-3 py-2 text-slate-400">{it.pathway}</td>
                      <td className="px-3 py-2">
                        <ERLBadge erl={{ erl_status: it.erl_status, erl_ref_id: it.erl_ref_id }} compact={false} />
                      </td>
                      <td className="px-3 py-2">
                        <ReconciliationBadge status={it.reconciliation_status} />
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-xs font-semibold ${
                          it.discrepancy_severity === "critical" ? "text-red-600" :
                          it.discrepancy_severity === "major" ? "text-orange-500" : "text-slate-400"
                        }`}>{it.discrepancy_severity === "none" ? "—" : it.discrepancy_severity}</span>
                      </td>
                      <td className="px-3 py-2">
                        {it.critical_flag_count > 0 ? <span className="text-xs text-red-600 font-semibold">{it.critical_flag_count}C</span> : <span className="text-xs text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        {it.submission_ready
                          ? <span className="text-xs text-emerald-600 font-semibold">✓</span>
                          : <span className="text-xs text-red-500 font-semibold">✗</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Export portfolio recon + snapshot */}
        <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/50 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs font-semibold text-slate-500 mr-1">Export Portfolio Report:</p>
            {["json","csv"].map(fmt => (
              <button key={fmt} onClick={() => handleReconExport(fmt)} disabled={exportingRecon[fmt] || loadingReport}
                className="flex items-center gap-1 px-3 h-8 text-xs font-medium border border-slate-200 rounded-lg bg-white hover:bg-slate-50 disabled:opacity-50"
                data-testid={`export-recon-${fmt}`}>
                <Download size={11} /> {fmt.toUpperCase()}
              </button>
            ))}
            <div className="flex items-center gap-2 ml-auto">
              <input value={snapNotes} onChange={e => setSnapNotes(e.target.value)}
                placeholder="Snapshot notes (optional)"
                className="h-8 px-3 text-xs border border-slate-200 rounded-lg focus:outline-none w-44"
                data-testid="prs-notes-input" />
              <button onClick={handleCreateSnapshot} disabled={creatingSnap || loadingReport}
                className="flex items-center gap-1 px-3 h-8 text-xs font-medium bg-[#0F172A] text-white rounded-lg hover:bg-slate-700 disabled:opacity-50"
                data-testid="create-prs-btn">
                <FileText size={11} className={creatingSnap ? "animate-pulse" : ""} />
                {creatingSnap ? "Freezing..." : "Freeze Snapshot"}
              </button>
            </div>
          </div>

          {/* Frozen snapshots list */}
          {snapshots.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-slate-400 font-semibold">Frozen Snapshots</p>
              {snapshots.slice(0, 5).map(s => (
                <div key={s.snapshot_id} className="flex items-center gap-3 bg-white rounded-lg border border-slate-200 px-3 py-1.5" data-testid={`prs-snapshot-${s.snapshot_id}`}>
                  <span className="font-mono text-xs text-slate-500">{s.snapshot_id}</span>
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded border ${
                    s.portfolio_submission_ready ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"
                  }`}>{s.portfolio_submission_ready ? "READY" : "NOT READY"}</span>
                  <span className="text-xs text-slate-400">{new Date(s.generated_at).toLocaleString()}</span>
                  <span className="text-xs text-slate-400 ml-auto">{s.summary?.total_itmos || 0} ITMOs</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── BTR ITMO Exports (existing) ─── */}
      <div>
        <p className="text-sm font-semibold text-slate-900 mb-3">BTR ITMO Exports (Article 6)</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[
            { fmt: "json", icon: FileText, title: "BTR-Ready JSON Export", desc: "Structured JSON conforming to Paris Agreement Article 6 BTR format.", color: "#3B82F6" },
            { fmt: "csv",  icon: Download, title: "Tabular CSV Export",     desc: "Flat-file CSV for spreadsheet tools and government registry imports.", color: "#059669" },
          ].map(({ fmt, icon: Icon, title, desc, color }) => (
            <div key={fmt} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`export-${fmt}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
                  <Icon size={14} style={{ color }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
                </div>
              </div>
              <button onClick={() => handleBTRExport(fmt)} disabled={exporting[fmt]}
                data-testid={`export-${fmt}-btn`}
                className="w-full h-9 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                style={{ backgroundColor: exporting[fmt] ? "#E2E8F0" : color, color: exporting[fmt] ? "#94A3B8" : "white" }}>
                {exporting[fmt] ? <RefreshCw size={13} className="animate-spin" /> : <Download size={13} />}
                {exporting[fmt] ? "Preparing..." : `Download ${fmt.toUpperCase()}`}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-600">
          All exports are internal audit instruments. CA values are indicative.
          BTR submission requires official host country sign-off. NOT READY label indicates
          unresolved reconciliation issues that must be addressed before formal submission.
        </p>
      </div>
    </div>
  );
}


// ── H. Audit Trail (CPIE Snapshots) ──────────────────────────────────────
const STATUS_COLOR = {
  pass: "bg-emerald-50 text-emerald-700 border-emerald-200",
  fail: "bg-red-50 text-red-700 border-red-200",
};
const RULE_THRESHOLD_TYPE = {
  hard_rule:              { label: "Hard Rule",          color: "text-slate-600" },
  internal_safety_limit:  { label: "Internal Threshold", color: "text-amber-600" },
  configurable_limit:     { label: "Configurable",       color: "text-blue-600"  },
};

function RuleResultRow({ rule }) {
  const [expanded, setExpanded] = useState(false);
  const isFail = rule.status === "fail";
  const ttMeta = RULE_THRESHOLD_TYPE[rule.threshold_type] || RULE_THRESHOLD_TYPE.hard_rule;

  return (
    <div className={`border rounded-xl overflow-hidden ${isFail ? "border-red-200" : "border-slate-200"}`}
      data-testid={`rule-result-${rule.rule_id}`}>
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50"
        onClick={() => setExpanded(e => !e)}>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${isFail ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
          {rule.status === "pass" ? "PASS" : "FAIL"}
        </span>
        <span className="text-xs font-mono text-slate-400">{rule.rule_id}</span>
        <span className="text-sm font-semibold text-slate-900 flex-1">{rule.rule_name}</span>
        {rule.threshold_type === "internal_safety_limit" && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200">Internal Threshold</span>
        )}
        {expanded ? <ChevronDown size={13} className="text-slate-400" /> : <ChevronRight size={13} className="text-slate-400" />}
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-4 pb-4 pt-3 bg-slate-50/40 space-y-3">
          {/* Calculation basis */}
          <div>
            <p className="text-xs text-slate-400 font-semibold uppercase mb-1">Calculation Basis</p>
            <p className="text-xs text-slate-900 bg-white rounded px-3 py-2 border border-slate-200">{rule.calculation_basis}</p>
          </div>

          {/* Data sources */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Data sources:</span>
            {(rule.data_sources || []).map(ds => (
              <span key={ds} className="text-xs font-mono px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{ds}</span>
            ))}
          </div>

          {/* Threshold context */}
          {rule.threshold_type && (
            <div className={`rounded-lg px-3 py-2 border ${rule.threshold_type === "internal_safety_limit" ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
              <p className={`text-xs font-semibold mb-0.5 ${ttMeta.color}`}>Threshold: {ttMeta.label}</p>
              <p className="text-xs text-slate-600">{rule.threshold_basis}</p>
              {rule.threshold_warning && (
                <div className="mt-2 flex items-start gap-1.5">
                  <AlertTriangle size={10} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 italic">{rule.threshold_warning}</p>
                </div>
              )}
            </div>
          )}

          {/* Affected projects */}
          {rule.affected_projects?.length > 0 && (
            <div>
              <p className="text-xs text-slate-400 font-semibold uppercase mb-1">Affected Projects</p>
              <div className="flex gap-1 flex-wrap">
                {rule.affected_projects.map(pid => (
                  <span key={pid} className="text-xs font-mono px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">{pid}</span>
                ))}
              </div>
            </div>
          )}

          {/* Linked flag */}
          {rule.linked_flag_id && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-400">Linked flag:</span>
              <span className="font-mono text-red-600">{rule.linked_flag_id}</span>
              <FlagStatusPill status={rule.flag_status_at_snapshot} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AuditTrailTab() {
  const [snapshots,  setSnapshots]  = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [detail,     setDetail]     = useState(null);
  const [attDetail,  setAttDetail]  = useState(null);
  const [submissions,setSubmissions]= useState([]);
  const [loading,    setLoading]    = useState(true);
  const [generating, setGenerating] = useState(false);
  const [notesForm,  setNotesForm]  = useState("");

  // Attestation form state (primary)
  const [showAttForm, setShowAttForm] = useState(false);
  const [attForm, setAttForm] = useState({
    role: "Compliance Officer",
    organization: "",
    attestation_statement: "I confirm, under my named authority and role, that: (1) this CPIE snapshot represents a true and complete state of the DeCarbonIQ system at the stated time of generation; (2) all five integrity rules were executed without override or manual intervention; (3) all active and unresolved integrity flags are disclosed in this report; (4) I am aware that RULE_004 thresholds are internal platform limits and do not represent official NDC allocations or regulatory compliance determinations.",
    signing_authority_level: "",
    delegation_scope: "",
    signer_organization_id: "",
  });
  const [attesting, setAttesting] = useState(false);
  const [orgOptions, setOrgOptions] = useState({ organizations: [], signing_authority_levels: [], delegation_scopes: [] });

  // Load org options for the attestation form
  useEffect(() => {
    client.get("/cai/ems/organizations").then(res => setOrgOptions(res.data)).catch(() => {});
  }, []);

  // Co-sign form state (secondary approver)
  const [showCoSignForm, setShowCoSignForm] = useState(false);
  const [coSignForm, setCoSignForm] = useState({
    role: "Program Director",
    organization: "",
    attestation_statement: "As secondary approver, I independently confirm that: (1) I have reviewed the CPIE snapshot and its rule-level evidence; (2) the integrity findings appear accurate and complete based on available information; (3) I approve this snapshot for external submission under the dual-attestation requirement; (4) I understand this is a system-level digital signature, not an external PKI signature.",
  });
  const [coSigning, setCoSigning] = useState(false);

  // Submission form state
  const [showSubForm, setShowSubForm]   = useState(false);
  const [subForm, setSubForm] = useState({ submitted_to: "", submission_date: new Date().toISOString().slice(0, 10), reference: "", channel: "email", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const ELIGIBLE_ROLES = ["Compliance Officer", "Program Director", "Authorized Signatory", "Government Liaison", "Portfolio Manager"];

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/cai/ems/audit-snapshots");
      setSnapshots(res.data.snapshots || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await client.post("/cai/ems/audit-snapshot", { notes: notesForm || null });
      toast.success(`${res.data.snapshot_id} generated — ${res.data.overall_status.toUpperCase()}`);
      setNotesForm("");
      loadList();
    } catch (err) { toast.error(err.response?.data?.detail || "Generation failed"); }
    setGenerating(false);
  };

  const handleView = async (snapId) => {
    if (selected === snapId) { setSelected(null); setDetail(null); setAttDetail(null); setSubmissions([]); setShowAttForm(false); setShowSubForm(false); setShowCoSignForm(false); return; }
    setSelected(snapId);
    setShowAttForm(false); setShowSubForm(false); setShowCoSignForm(false); setSubmissions([]);
    try {
      const [detRes, attRes, subRes] = await Promise.allSettled([
        client.get(`/cai/ems/audit-snapshots/${snapId}`),
        client.get(`/cai/ems/audit-snapshots/${snapId}/attestation`),
        client.get("/cai/ems/submissions"),
      ]);
      if (detRes.status === "fulfilled") setDetail(detRes.value.data);
      if (attRes.status === "fulfilled") setAttDetail(attRes.value.data);
      if (subRes.status === "fulfilled") {
        setSubmissions((subRes.value.data.submissions || []).filter(s => s.snapshot_id === snapId));
      }
    } catch { toast.error("Failed to load snapshot detail"); }
  };

  const handleAttest = async () => {
    if (!attForm.organization || attForm.organization.trim().length < 2) { toast.warning("Organization is required."); return; }
    if (attForm.attestation_statement.trim().length < 50) { toast.warning("Statement must be at least 50 characters."); return; }
    setAttesting(true);
    try {
      const res = await client.post(`/cai/ems/audit-snapshots/${selected}/attest`, {
        ...attForm,
        signing_authority_level: attForm.signing_authority_level || undefined,
        delegation_scope:        attForm.delegation_scope || undefined,
        signer_organization_id:  attForm.signer_organization_id ? parseInt(attForm.signer_organization_id) : undefined,
      });
      const isDual = res.data.attestation_mode === "dual";
      toast.success(isDual
        ? `${res.data.attestation_id} — Primary signature complete. Awaiting secondary approver.`
        : `${res.data.attestation_id} — Snapshot attested. Chain of custody established.`);
      setShowAttForm(false);
      const attRes = await client.get(`/cai/ems/audit-snapshots/${selected}/attestation`);
      setAttDetail(attRes.data);
      loadList();
    } catch (err) {
      const d = err.response?.data;
      toast.error(typeof d === "object" ? (d.detail || d.message || "Attestation failed") : String(d || "Attestation failed"));
    }
    setAttesting(false);
  };

  const handleCoSign = async () => {
    if (!coSignForm.organization || coSignForm.organization.trim().length < 2) { toast.warning("Organization is required."); return; }
    if (coSignForm.attestation_statement.trim().length < 50) { toast.warning("Statement must be at least 50 characters."); return; }
    setCoSigning(true);
    try {
      const res = await client.post(`/cai/ems/audit-snapshots/${selected}/co-sign`, coSignForm);
      toast.success(`${res.data.attestation_id} — Dual attestation complete. Submission permitted.`);
      setShowCoSignForm(false);
      const attRes = await client.get(`/cai/ems/audit-snapshots/${selected}/attestation`);
      setAttDetail(attRes.data);
      loadList();
    } catch (err) {
      const d = err.response?.data;
      const errMsg = typeof d?.detail === "string" ? d.detail : (d?.detail?.message || d?.message || "Co-sign failed");
      toast.error(String(errMsg));
    }
    setCoSigning(false);
  };

  const handleRevoke = async () => {
    const reason = window.prompt("Enter revocation reason (required, min 10 chars):");
    if (!reason || reason.trim().length < 10) { toast.warning("Revocation requires a reason of at least 10 characters."); return; }
    try {
      await client.post(`/cai/ems/audit-snapshots/${selected}/revoke`, { revocation_reason: reason });
      toast.success("Attestation revoked. Snapshot is now un-attested.");
      const attRes = await client.get(`/cai/ems/audit-snapshots/${selected}/attestation`);
      setAttDetail(attRes.data);
      loadList();
    } catch (err) { toast.error(err.response?.data?.detail || "Revocation failed"); }
  };

  const handleSubmit = async () => {
    if (!subForm.submitted_to.trim()) { toast.warning("Submitted-to is required."); return; }
    setSubmitting(true);
    try {
      const res = await client.post("/cai/ems/submissions", { ...subForm, snapshot_id: selected });
      toast.success(`${res.data.submission_id} — Submission recorded.`);
      setShowSubForm(false);
      loadList();
    } catch (err) {
      const d = err.response?.data;
      toast.error(typeof d === "object" ? (d.message || d.detail || "Submission failed") : String(d || "Submission failed"));
    }
    setSubmitting(false);
  };

  const handleExport = async (snapId) => {
    try {
      const res = await client.get(`/cai/ems/audit-snapshots/${snapId}/export`, { responseType: "blob" });
      const isAtt = snapshots.find(s => s.snapshot_id === snapId)?.is_attested;
      const fname = `${snapId}${isAtt ? "_ATTESTED" : "_UNATTESTED"}.json`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a   = document.createElement("a"); a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported: ${fname}`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-5" data-testid="audit-trail-tab">
      {/* Header + generate */}
      <SectionCard>

        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-900">CPIE Audit Snapshots & Chain of Custody</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Versioned, immutable audit records — generate, attest, and submit. Each snapshot captures point-in-time evidence of all 5 CPIE rules.
            Attestation adds formal accountability. Submission records the external handoff.
          </p>
        </div>

        {/* Generate form */}
        <div className="flex items-end gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
          <div className="flex-1">
            <label className="text-xs text-slate-400 mb-1 block">Notes (optional)</label>
            <input value={notesForm} onChange={e => setNotesForm(e.target.value)}
              placeholder="e.g. Pre-submission audit, Q1 2026 review, Investor due diligence..."
              className="w-full h-9 px-3 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-[#059669] bg-white"
              data-testid="snapshot-notes-input" />
          </div>
          <button onClick={handleGenerate} disabled={generating}
            className="flex items-center gap-1.5 px-4 h-9 bg-[#0F172A] text-white text-xs font-semibold rounded-xl hover:bg-slate-700 disabled:opacity-50 flex-shrink-0"
            data-testid="generate-snapshot-btn">
            <FileText size={12} className={generating ? "animate-pulse" : ""} />
            {generating ? "Generating..." : "Generate Snapshot"}
          </button>
        </div>

        <div className="mt-3 flex items-start gap-2 text-xs text-slate-400">
          <Info size={10} className="flex-shrink-0 mt-0.5" />
          <span>
            Snapshots are internal audit instruments. RULE_004 = platform-defined limit, not regulatory. No external registry reconciliation.
            Generate → Attest (with named role + signature hash) → Submit (external record).
          </span>
        </div>
      
      </SectionCard>

      {/* Snapshot list */}
      {loading ? (
        <div className="space-y-2">{[1,2].map(i=><div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
      ) : snapshots.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
          <History size={28} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm font-semibold text-slate-500">No audit snapshots yet</p>
          <p className="text-xs text-slate-400 mt-1">Generate a snapshot to begin the chain of custody.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {snapshots.map(s => {
            const isSelected   = selected === s.snapshot_id;
            const isAtt        = s.is_attested;
            const isPartial    = s.is_partially_signed;
            const isDual       = s.attestation_mode === "dual";
            return (
              <div key={s.snapshot_id}
                className={`bg-white border rounded-xl overflow-hidden ${isSelected ? "border-teal-300 shadow-sm" : "border-slate-200"}`}
                data-testid={`snapshot-${s.snapshot_id}`}>

                {/* Header row */}
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50"
                  onClick={() => handleView(s.snapshot_id)}>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${STATUS_COLOR[s.overall_status] || "bg-slate-100 text-slate-500 border-slate-200"}`}>
                    {s.overall_status?.toUpperCase()}
                  </span>
                  <span className="text-xs font-mono font-semibold text-slate-900">{s.snapshot_id}</span>
                  <span className="text-xs text-slate-400 flex-1">{new Date(s.generated_at).toLocaleString()}</span>

                  {/* Attestation status badge */}
                  {isAtt ? (
                    <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                      data-testid={`att-badge-${s.snapshot_id}`}>
                      <CheckCheck size={9} />
                      {isDual ? `Dual Attested (${s.signed_count}/${s.required_signers})` : "Attested"}
                    </span>
                  ) : isPartial ? (
                    <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                      data-testid={`partial-badge-${s.snapshot_id}`}>
                      <Clock size={9} /> Partially Signed (1/{s.required_signers})
                    </span>
                  ) : (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200"
                      data-testid={`unatt-badge-${s.snapshot_id}`}>
                      Not Attested
                    </span>
                  )}

                  <div className="flex items-center gap-1.5 text-xs text-slate-400">
                    {s.critical_flags_count > 0 && <span className="text-red-600 font-semibold">C:{s.critical_flags_count}</span>}
                    {s.high_flags_count > 0     && <span className="text-orange-600 font-semibold">H:{s.high_flags_count}</span>}
                    {s.submission_count > 0     && <span className="text-teal-600">Sub:{s.submission_count}</span>}
                  </div>
                  <button onClick={e => { e.stopPropagation(); handleExport(s.snapshot_id); }}
                    className={`flex items-center gap-1 px-2 h-7 border text-xs rounded-lg hover:opacity-80 ${
                      isAtt ? "border-emerald-200 text-emerald-600 bg-emerald-50" :
                      isPartial ? "border-amber-200 text-amber-700 bg-amber-50" :
                      "border-slate-200 text-slate-500 bg-slate-50"}`}
                    data-testid={`export-snapshot-${s.snapshot_id}`}>
                    <Download size={10} /> {isAtt ? "JSON" : isPartial ? "JSON±" : "JSON*"}
                  </button>
                  {isSelected ? <ChevronDown size={13} className="text-slate-400" /> : <ChevronRight size={13} className="text-slate-400" />}
                </div>

                {/* Expanded: evidence + attestation + submission */}
                {isSelected && detail && (
                  <div className="border-t border-slate-100 px-4 pb-5 pt-3 space-y-4 bg-slate-50/30">
                    {/* Portfolio state */}
                    {detail.portfolio_state && (
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                          ["Projects",     detail.portfolio_state.total_projects],
                          ["Authorized",   detail.portfolio_state.authorized_projects],
                          ["ITMO Volume",  `${detail.portfolio_state.total_itmo_vol} tCO₂e`],
                          ["ITMO Coverage",`${detail.portfolio_state.itmo_coverage_pct}%`],
                        ].map(([l, v]) => (
                          <div key={l} className="bg-white rounded-lg px-3 py-2 border border-slate-200 text-center">
                            <p className="text-xs text-slate-400">{l}</p>
                            <p className="text-sm font-semibold text-slate-900">{v}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Rule evidence */}
                    <div>
                      <p className="text-xs text-slate-400 font-semibold uppercase mb-2">Rule Evidence (5 rules)</p>
                      <div className="space-y-1.5">
                        {(detail.rules || []).map(r => <RuleResultRow key={r.rule_id} rule={r} />)}
                      </div>
                    </div>

                    {/* Attestation section */}
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className={`px-4 py-2.5 border-b flex items-center justify-between ${
                        isAtt ? "bg-emerald-50 border-emerald-200" :
                        isPartial ? "bg-amber-50 border-amber-200" :
                        "bg-slate-50 border-slate-200"
                      }`}>
                        <div className="flex items-center gap-2">
                          {isAtt ? <CheckCheck size={13} className="text-emerald-600" /> :
                           isPartial ? <Clock size={13} className="text-amber-500" /> :
                           <AlertTriangle size={13} className="text-slate-400" />}
                          <p className={`text-xs font-bold ${isAtt ? "text-emerald-700" : isPartial ? "text-amber-700" : "text-slate-600"}`}>
                            {isAtt ? (isDual ? "Dual Attestation Complete" : "Formally Attested") :
                             isPartial ? "Awaiting Secondary Approver (1 of 2 signatures)" :
                             "Attestation Required for External Use"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {!isAtt && !isPartial && !showAttForm && (
                            <button onClick={() => setShowAttForm(true)} data-testid="open-attest-form-btn"
                              className="flex items-center gap-1 px-3 h-7 bg-[#0F172A] text-white text-xs font-semibold rounded-lg hover:bg-slate-700">
                              <Shield size={10} /> Attest Snapshot
                            </button>
                          )}
                          {isPartial && !showCoSignForm && (
                            <button onClick={() => setShowCoSignForm(true)} data-testid="open-cosign-form-btn"
                              className="flex items-center gap-1 px-3 h-7 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700">
                              <Shield size={10} /> Co-Sign (Secondary)
                            </button>
                          )}
                          {(isAtt || isPartial) && attDetail?.active && (
                            <button onClick={handleRevoke}
                              className="text-xs text-red-500 border border-red-200 rounded-lg px-2 h-7 hover:bg-red-50"
                              data-testid="revoke-attestation-btn">
                              Revoke
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="p-4">
                        {(isAtt || isPartial) && attDetail?.active ? (
                          <div className="space-y-3 text-xs">
                            {/* Primary signer */}
                            <div>
                              <p className="text-xs text-slate-400 font-semibold uppercase mb-1.5">Primary Attestor</p>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  ["Attestation ID",     attDetail.active.attestation_id],
                                  ["Signed by",          attDetail.active.attested_by],
                                  ["Role",               attDetail.active.role],
                                  ["Organization",       attDetail.active.organization_name || attDetail.active.organization],
                                  ["Authority Level",    attDetail.active.signing_authority_level || "—"],
                                  ["Delegation Scope",   attDetail.active.delegation_scope || "—"],
                                  ["Signed at",          attDetail.active.signed_at ? new Date(attDetail.active.signed_at).toLocaleString() : "—"],
                                  ["Signature",          attDetail.active.signature_hash?.slice(0, 16) + "..."],
                                ].map(([l, v]) => (
                                  <div key={l}><span className="text-slate-400">{l}:</span> <strong className="text-slate-900">{v}</strong></div>
                                ))}
                              </div>
                              <div className="bg-slate-50 rounded px-3 py-1.5 border border-slate-200 mt-1.5">
                                <p className="text-xs text-slate-400 mb-0.5">Statement</p>
                                <p className="text-xs text-slate-600 italic line-clamp-2">{attDetail.active.attestation_statement}</p>
                              </div>
                            </div>

                            {/* Secondary signer (dual mode) */}
                            {attDetail.active.co_signer && attDetail.active.co_signer.co_signed_by && (
                              <div className="border-t border-slate-100 pt-3">
                                <p className="text-xs text-slate-400 font-semibold uppercase mb-1.5">Secondary Approver</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {[
                                    ["Co-signed by",  attDetail.active.co_signer.co_signed_by],
                                    ["Role",          attDetail.active.co_signer.co_signer_role],
                                    ["Organization",  attDetail.active.co_signer.co_organization],
                                    ["Co-signed at",  attDetail.active.co_signer.co_signed_at ? new Date(attDetail.active.co_signer.co_signed_at).toLocaleString() : "—"],
                                    ["Co-signature",  attDetail.active.co_signer.co_signature_hash?.slice(0, 16) + "..."],
                                  ].filter(([,v]) => v).map(([l, v]) => (
                                    <div key={l}><span className="text-slate-400">{l}:</span> <strong className="text-slate-900">{v}</strong></div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Awaiting co-sign notice */}
                            {isPartial && (
                              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
                                <Clock size={12} className="text-amber-500 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-xs font-semibold text-amber-700">Awaiting Secondary Approver</p>
                                  <p className="text-xs text-amber-600 mt-0.5">This attestation requires a second, independent co-signature before submission is permitted. The co-signer must be a different person from the primary attestor.</p>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : !showAttForm ? (
                          <div className="text-center py-3 text-xs text-slate-400">
                            <p>This snapshot has not been formally attested.</p>
                            <p className="mt-0.5">Attestation is required for external regulatory submission or investor disclosure.</p>
                          </div>
                        ) : null}

                        {/* Primary Attestation form */}
                        {showAttForm && !isAtt && !isPartial && (
                          <div className="space-y-3 mt-2" data-testid="attestation-form">
                            <p className="text-xs font-semibold text-slate-900">Primary Attestation — Organizational Authority</p>
                            {/* Row 1: Role + Organization */}
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-xs text-slate-400 mb-0.5 block">Role *</label>
                                <select value={attForm.role} onChange={e => setAttForm(f => ({ ...f, role: e.target.value }))}
                                  className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
                                  data-testid="att-role-select">
                                  {ELIGIBLE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="text-xs text-slate-400 mb-0.5 block">Organization Name *</label>
                                <input value={attForm.organization} onChange={e => setAttForm(f => ({ ...f, organization: e.target.value }))}
                                  placeholder="DeCarbonIQ / Your Organization"
                                  className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg focus:outline-none"
                                  data-testid="att-org-input" />
                              </div>
                            </div>
                            {/* Row 2: Pre-registered org + signing authority */}
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-xs text-slate-400 mb-0.5 block">Link to Registered Organization</label>
                                <select value={attForm.signer_organization_id} onChange={e => setAttForm(f => ({ ...f, signer_organization_id: e.target.value }))}
                                  className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
                                  data-testid="att-org-id-select">
                                  <option value="">None (use name above)</option>
                                  {orgOptions.organizations.map(o => <option key={o.id} value={o.id}>{o.name} ({o.type})</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="text-xs text-slate-400 mb-0.5 block">Signing Authority Level</label>
                                <select value={attForm.signing_authority_level} onChange={e => setAttForm(f => ({ ...f, signing_authority_level: e.target.value }))}
                                  className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
                                  data-testid="att-authority-select">
                                  <option value="">Select authority level...</option>
                                  {orgOptions.signing_authority_levels?.map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                              </div>
                            </div>
                            {/* Row 3: Delegation scope */}
                            <div>
                              <label className="text-xs text-slate-400 mb-0.5 block">Delegation Scope</label>
                              <select value={attForm.delegation_scope} onChange={e => setAttForm(f => ({ ...f, delegation_scope: e.target.value }))}
                                className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
                                data-testid="att-delegation-select">
                                <option value="">Select delegation scope...</option>
                                {orgOptions.delegation_scopes?.map(s => <option key={s.key} value={s.key}>{s.key} — {s.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-slate-400 mb-0.5 block">Statement * (min 50 chars)</label>
                              <textarea value={attForm.attestation_statement}
                                onChange={e => setAttForm(f => ({ ...f, attestation_statement: e.target.value }))}
                                rows={3} className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg resize-none focus:outline-none"
                                data-testid="att-statement-input" />
                            </div>
                            <div className="flex gap-2">
                              <button onClick={handleAttest} disabled={attesting}
                                className="flex-1 h-9 bg-[#059669] text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                                data-testid="submit-attestation-btn">
                                {attesting ? "Signing..." : "Sign & Attest"}
                              </button>
                              <button onClick={() => setShowAttForm(false)} className="px-4 h-9 text-slate-500 text-xs border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                            </div>
                            <p className="text-xs text-slate-400 italic">SHA-256 hash binds: snapshot + signer identity + organization + authority level + delegation scope. DeCarbonIQ internal — not an external PKI signature.</p>
                          </div>
                        )}

                        {/* Co-sign form (secondary approver) */}
                        {showCoSignForm && isPartial && (
                          <div className="space-y-3 mt-2 border border-amber-200 rounded-xl p-3 bg-amber-50/30" data-testid="cosign-form">
                            <p className="text-xs font-semibold text-amber-700">Secondary Approver Co-Signature</p>
                            <p className="text-xs text-amber-600">You must be a different person from the primary attestor. Provide an independent statement.</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-xs text-slate-400 mb-0.5 block">Role *</label>
                                <select value={coSignForm.role} onChange={e => setCoSignForm(f => ({ ...f, role: e.target.value }))}
                                  className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
                                  data-testid="cosign-role-select">
                                  {ELIGIBLE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="text-xs text-slate-400 mb-0.5 block">Organization *</label>
                                <input value={coSignForm.organization} onChange={e => setCoSignForm(f => ({ ...f, organization: e.target.value }))}
                                  placeholder="Your Organization"
                                  className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg focus:outline-none"
                                  data-testid="cosign-org-input" />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-slate-400 mb-0.5 block">Independent Statement * (min 50 chars)</label>
                              <textarea value={coSignForm.attestation_statement}
                                onChange={e => setCoSignForm(f => ({ ...f, attestation_statement: e.target.value }))}
                                rows={3} className="w-full px-3 py-2 text-xs border border-amber-200 rounded-lg resize-none focus:outline-none bg-white"
                                data-testid="cosign-statement-input" />
                            </div>
                            <div className="flex gap-2">
                              <button onClick={handleCoSign} disabled={coSigning}
                                className="flex-1 h-9 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50"
                                data-testid="submit-cosign-btn">
                                {coSigning ? "Co-Signing..." : "Co-Sign & Complete Attestation"}
                              </button>
                              <button onClick={() => setShowCoSignForm(false)} className="px-4 h-9 text-slate-500 text-xs border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Submission section */}
                    {isAtt && (
                      <div className="border border-teal-200 rounded-xl overflow-hidden">
                        <div className="bg-teal-50 px-4 py-2.5 border-b border-teal-200 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ExternalLink size={13} className="text-teal-600" />
                            <p className="text-xs font-bold text-teal-700">External Submission Record</p>
                          </div>
                          {!showSubForm && (
                            <button onClick={() => setShowSubForm(true)} data-testid="open-submission-form-btn"
                              className="flex items-center gap-1 px-3 h-7 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700">
                              <Plus size={10} /> Record Submission
                            </button>
                          )}
                        </div>
                        <div className="p-4">
                          {/* Existing submissions */}
                          {submissions.length > 0 && (
                            <div className="space-y-2 mb-3">
                              {submissions.map(sub => (
                                <div key={sub.submission_id} className="border border-slate-200 rounded-lg p-2.5 bg-white" data-testid={`sub-row-${sub.submission_id}`}>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-mono font-semibold text-teal-700">{sub.submission_id}</span>
                                    <span className="text-xs text-slate-500">{sub.submitted_to}</span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded border font-semibold ${sub.status === "acknowledged" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-600 border-blue-200"}`}>
                                      {sub.status}
                                    </span>
                                    <ERLBadge erl={sub.erl} compact={false} />
                                    {sub.reference && <span className="text-xs text-slate-400">{sub.reference}</span>}
                                  </div>
                                  {/* ERL link for this submission */}
                                  {sub.erl?.erl_status === "not_linked" && (
                                    <ERLLinkForm
                                      entityType="submission"
                                      entityId={sub.submission_id}
                                      currentErl={sub.erl}
                                      onSave={() => handleView(selected)}
                                      onClose={() => {}}
                                    />
                                  )}
                                  {sub.erl?.erl_status === "mismatched" && (
                                    <div className="mt-1 flex items-start gap-1 text-xs text-red-600">
                                      <AlertCircle size={9} className="mt-0.5 flex-shrink-0" />
                                      Mismatch detected — external reference does not align with internal record. Review immediately.
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {!showSubForm && submissions.length === 0 ? (
                            <p className="text-xs text-slate-400 text-center py-2">
                              Record when and to whom this attested snapshot was submitted externally.
                            </p>
                          ) : null}
                          {showSubForm && (
                            <div className="space-y-3" data-testid="submission-form">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-xs text-slate-400 mb-0.5 block">Submitted To *</label>
                                  <input value={subForm.submitted_to} onChange={e => setSubForm(f => ({ ...f, submitted_to: e.target.value }))}
                                    placeholder="UNFCCC Registry / Ministry of Environment"
                                    className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg focus:outline-none"
                                    data-testid="sub-submitted-to-input" />
                                </div>
                                <div>
                                  <label className="text-xs text-slate-400 mb-0.5 block">Submission Date *</label>
                                  <input type="date" value={subForm.submission_date} onChange={e => setSubForm(f => ({ ...f, submission_date: e.target.value }))}
                                    className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
                                    data-testid="sub-date-input" />
                                </div>
                                <div>
                                  <label className="text-xs text-slate-400 mb-0.5 block">External Reference</label>
                                  <input value={subForm.reference} onChange={e => setSubForm(f => ({ ...f, reference: e.target.value }))}
                                    placeholder="BTR-2026-XYZ"
                                    className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg focus:outline-none"
                                    data-testid="sub-reference-input" />
                                </div>
                                <div>
                                  <label className="text-xs text-slate-400 mb-0.5 block">Channel</label>
                                  <select value={subForm.channel} onChange={e => setSubForm(f => ({ ...f, channel: e.target.value }))}
                                    className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none">
                                    {["email","registry","in_person","portal","courier"].map(c => <option key={c} value={c}>{c}</option>)}
                                  </select>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button onClick={handleSubmit} disabled={submitting}
                                  className="flex-1 h-9 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50"
                                  data-testid="record-submission-btn">
                                  {submitting ? "Recording..." : "Record Submission"}
                                </button>
                                <button onClick={() => setShowSubForm(false)}
                                  className="px-4 h-9 text-slate-500 text-xs border border-slate-200 rounded-lg hover:bg-slate-50">
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {detail.notes && (
                      <p className="text-xs text-slate-500 italic border-t border-slate-100 pt-2">Notes: {detail.notes}</p>
                    )}
                    <p className="text-xs text-slate-300 italic">
                      Generated by {s.generated_by_label} · {s.snapshot_id} is immutable · Signature: SHA-256 hash (DeCarbonIQ internal — not a legally binding digital signature)
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ── G. Portfolio Risk (CPIE) ───────────────────────────────────────────────
const RULE_META = {
  RULE_001: { label: "Volume Over-Export",        icon: AlertCircle,   color: "#DC2626" },
  RULE_002: { label: "Sector Breach",             icon: X,             color: "#F97316" },
  RULE_003: { label: "Duplicate Source Activity", icon: AlertTriangle, color: "#DC2626" },
  RULE_004: { label: "Country Threshold",         icon: Globe,         color: "#F97316" },
  RULE_005: { label: "Unauthorized ITMO",         icon: Shield,        color: "#F97316" },
};

function PortfolioRiskTab() {
  const [summary,       setSummary]       = useState(null);
  const [portfolioFlags,setPortfolioFlags] = useState([]);
  const [schedStatus,   setSchedStatus]   = useState(null);  // for RULE_004 threshold
  const [loading,       setLoading]       = useState(true);
  const [scanning,      setScanning]      = useState(false);
  const [editThreshold, setEditThreshold] = useState(false);
  const [thresholdForm, setThresholdForm] = useState("80");
  const [savingThreshold, setSavingThreshold] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, flagRes, schedRes] = await Promise.allSettled([
        client.get("/cai/ems/summary"),
        client.get("/cai/ems/portfolio-flags"),
        client.get("/cai/scheduler/status"),
      ]);
      if (sumRes.status  === "fulfilled") setSummary(sumRes.value.data);
      if (flagRes.status === "fulfilled") setPortfolioFlags(flagRes.value.data.flags || []);
      if (schedRes.status === "fulfilled") {
        setSchedStatus(schedRes.value.data);
        setThresholdForm(String(schedRes.value.data.rule_004_threshold_pct || 80));
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await client.post("/cai/ems/portfolio-scan");
      toast.success(`CPIE scan complete — ${res.data.flags_raised} rule(s) evaluated`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Scan failed"); }
    setScanning(false);
  };

  const saveThreshold = async () => {
    const val = parseInt(thresholdForm);
    if (isNaN(val) || val < 1 || val > 100) { toast.warning("Threshold must be 1–100."); return; }
    setSavingThreshold(true);
    try {
      await client.patch("/cai/scheduler/config", { rule_004_threshold_pct: val });
      toast.success(`RULE_004 threshold updated to ${val}%`);
      setEditThreshold(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    setSavingThreshold(false);
  };

  const pr = summary?.portfolio_risk || {};
  const hasRisk = (pr.total_portfolio_active || 0) > 0;
  const isBlocked = (pr.critical_portfolio || 0) > 0;

  const rulesStatus = {
    RULE_001: portfolioFlags.filter(f => f.rule_id === "RULE_001"),
    RULE_002: portfolioFlags.filter(f => f.rule_id?.startsWith("RULE_002")),
    RULE_003: portfolioFlags.filter(f => f.rule_id?.startsWith("RULE_003")),
    RULE_004: portfolioFlags.filter(f => f.rule_id?.startsWith("RULE_004")),
    RULE_005: portfolioFlags.filter(f => f.rule_id === "RULE_005"),
  };

  return (
    <div className="space-y-5" data-testid="portfolio-risk-tab">
      {/* Banner */}
      {!loading && (
        <div className={`rounded-xl border-2 p-4 flex items-start gap-3 ${
          isBlocked ? "bg-red-50 border-red-400" : hasRisk ? "bg-orange-50 border-orange-300" : "bg-emerald-50 border-emerald-300"
        }`} data-testid="portfolio-risk-banner">
          {isBlocked
            ? <Lock size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
            : hasRisk
              ? <AlertTriangle size={18} className="text-orange-500 flex-shrink-0 mt-0.5" />
              : <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0 mt-0.5" />}
          <div className="flex-1">
            <p className={`text-sm font-bold ${isBlocked ? "text-red-700" : hasRisk ? "text-orange-700" : "text-emerald-700"}`}>
              {isBlocked
                ? `Critical portfolio risk — ${pr.critical_portfolio} rule(s) violated. ITMO creation blocked.`
                : hasRisk
                  ? `Portfolio risk detected — ${pr.total_portfolio_active} active portfolio flag(s)`
                  : "Portfolio integrity: No cross-project inconsistencies detected"}
            </p>
            <p className={`text-xs mt-0.5 ${isBlocked ? "text-red-600" : hasRisk ? "text-orange-600" : "text-emerald-600"}`}>
              {isBlocked
                ? "All critical portfolio rules must be resolved before new ITMO transfers can be authorized."
                : hasRisk
                  ? "High-severity portfolio flags trigger BTR export warnings. Review affected projects."
                  : "All 5 CPIE rules are satisfied. Cross-project integrity is maintained."}
            </p>
          </div>
          <button onClick={runScan} disabled={scanning}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-current rounded-lg opacity-70 hover:opacity-100 flex-shrink-0"
            data-testid="portfolio-scan-btn">
            <ScanLine size={11} className={scanning ? "animate-pulse" : ""} />
            {scanning ? "Scanning..." : "Run CPIE"}
          </button>
        </div>
      )}

      {/* Rule summary grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {Object.entries(RULE_META).map(([ruleId, meta]) => {
          const ruleFlags = rulesStatus[ruleId] || [];
          const active = ruleFlags.filter(f => ["open","under_investigation"].includes(f.status));
          const Icon = meta.icon;
          return (
            <div key={ruleId}
              className={`bg-white border rounded-xl p-3 ${active.length > 0 ? "border-orange-200" : "border-slate-200"}`}
              data-testid={`rule-card-${ruleId}`}>
              <div className="flex items-center gap-1.5 mb-2">
                <Icon size={13} style={{ color: active.length > 0 ? meta.color : "#94A3B8" }} />
                <span className="text-xs font-mono text-slate-400">{ruleId}</span>
              </div>
              <p className="text-[11px] font-semibold text-slate-900 leading-snug">{meta.label}</p>
              {active.length > 0
                ? <p className="text-xs text-red-600 mt-1 font-semibold">{active.length} active flag(s)</p>
                : <p className="text-xs text-emerald-500 mt-1 flex items-center gap-0.5"><Check size={9} strokeWidth={3} /> Compliant</p>}
            </div>
          );
        })}
      </div>

      {/* Portfolio flags list */}
      {loading ? (
        <div className="space-y-2">{[1,2].map(i=><div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
      ) : portfolioFlags.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
          <CheckCircle2 size={28} className="mx-auto text-emerald-400 mb-2" />
          <p className="text-sm font-semibold text-slate-600">No portfolio-level issues detected</p>
          <p className="text-xs text-slate-400 mt-1">Run CPIE scan to check cross-project integrity.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-slate-400">{portfolioFlags.length} portfolio flag(s)</p>
          {portfolioFlags.map(f => {
            const isActive = ["open","under_investigation"].includes(f.status);
            return (
              <div key={f.flag_id}
                className={`bg-white rounded-xl border-l-4 p-4 border border-r border-t border-b ${f.severity === "critical" ? "border-l-red-500" : "border-l-orange-400"}`}
                data-testid={`portfolio-flag-${f.flag_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-slate-400">{f.flag_id}</span>
                      <SeverityBadge severity={f.severity} />
                      <FlagStatusPill status={f.status} />
                      {f.rule_id && <span className="text-xs font-mono px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{f.rule_id}</span>}
                    </div>
                    <p className="text-sm font-semibold text-slate-900">{f.title}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mb-2">{f.description}</p>
                {f.impact && (
                  <div className="bg-amber-50 border border-amber-100 rounded px-2.5 py-1.5 mb-2">
                    <p className="text-xs text-amber-700">{f.impact}</p>
                  </div>
                )}
                {f.affected_projects?.length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-xs text-slate-400">Affected:</span>
                    {f.affected_projects.map(pid => (
                      <span key={pid} className="text-xs font-mono px-1.5 py-0.5 bg-teal-50 text-teal-700 border border-teal-200 rounded">{pid}</span>
                    ))}
                  </div>
                )}
                {f.country_context && (
                  <p className="text-xs text-slate-400 mt-1">Country: {f.country_context}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* RULE_004 Threshold Configuration */}
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="rule004-config">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <p className="text-xs font-semibold text-slate-900">RULE_004 — Country Threshold</p>
            <p className="text-xs text-slate-400 mt-0.5">
              Internal safety limit for country-level ITMO export. Platform-defined only.
              <span className="text-amber-600 font-semibold ml-1">NOT an official NDC allocation or regulatory cap.</span>
            </p>
          </div>
          {!editThreshold ? (
            <button onClick={() => setEditThreshold(true)} data-testid="edit-rule004-btn"
              className="flex-shrink-0 text-xs text-slate-500 border border-slate-200 rounded-lg px-2.5 py-1 hover:bg-slate-50">
              Edit
            </button>
          ) : (
            <button onClick={() => setEditThreshold(false)}
              className="flex-shrink-0 text-xs text-slate-400 hover:text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1">
              Cancel
            </button>
          )}
        </div>

        {!editThreshold ? (
          <div className="flex items-center gap-3">
            <div className="text-2xl font-bold text-slate-900">{schedStatus?.rule_004_threshold_pct || 80}%</div>
            <div>
              <p className="text-xs text-slate-500">of country verified reductions</p>
              <p className="text-xs text-amber-600 italic">Internal limit — not regulatory</p>
            </div>
          </div>
        ) : (
          <div className="flex items-end gap-3 mt-2" data-testid="rule004-edit-form">
            <div className="flex-1">
              <label className="text-xs text-slate-400 mb-1 block">Threshold % (1–100)</label>
              <input type="number" min="1" max="100" value={thresholdForm}
                onChange={e => setThresholdForm(e.target.value)}
                className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#059669]"
                data-testid="rule004-threshold-input" />
            </div>
            <button onClick={saveThreshold} disabled={savingThreshold}
              className="h-9 px-4 bg-[#059669] text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              data-testid="rule004-save-btn">
              {savingThreshold ? "Saving..." : "Save"}
            </button>
          </div>
        )}
      </div>

      {/* CPIE methodology */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-xs font-semibold text-slate-900 mb-2">Cross-Project Integrity Rules</p>
        <div className="space-y-1.5">
          {[
            ["RULE_001","Total portfolio ITMO volume must not exceed total verified reductions"],
            ["RULE_002","ITMO sector must comply with bilateral agreement sector restrictions"],
            ["RULE_003","No project may share a baseline assessment with another project"],
            ["RULE_004","Country ITMO exports must stay within configurable% of verified capacity (internal threshold — NOT an official NDC allocation)"],
            ["RULE_005","Only fully authorized projects may contribute ITMOs to the portfolio"],
          ].map(([r, d]) => (
            <div key={r} className="flex items-start gap-2 text-xs">
              <span className="font-mono text-slate-400 flex-shrink-0 w-16">{r}</span>
              <span className="text-slate-500">{d}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


const EMS_STATUS_CFG = {
  open:               { label: "Open",               color: "bg-red-50 text-red-700 border-red-200",         icon: AlertCircle },
  under_investigation:{ label: "Investigating",       color: "bg-amber-50 text-amber-700 border-amber-200",   icon: Eye         },
  resolved:           { label: "Resolved",            color: "bg-blue-50 text-blue-700 border-blue-200",      icon: CheckCircle2},
  verified:           { label: "Verified",            color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCheck },
  dismissed:          { label: "Dismissed",           color: "bg-slate-100 text-slate-500 border-slate-200",  icon: XCircle     },
};
const SEV_CFG = {
  critical: { color: "bg-red-600 text-white",          ring: "border-red-300" },
  high:     { color: "bg-orange-500 text-white",        ring: "border-orange-200" },
  medium:   { color: "bg-amber-400 text-white",         ring: "border-amber-200" },
  low:      { color: "bg-slate-300 text-slate-700",     ring: "border-slate-200" },
};

function SeverityBadge({ severity }) {
  const cfg = SEV_CFG[severity] || SEV_CFG.medium;
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${cfg.color}`}>{severity}</span>;
}
function FlagStatusPill({ status }) {
  const cfg = EMS_STATUS_CFG[status] || EMS_STATUS_CFG.open;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}>
      <Icon size={9} /> {cfg.label}
    </span>
  );
}



// ── External Reconciliation ───────────────────────────────────────────────
const RECON_STATUS_CFG = {
  matched:       { label: "Matched",       color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  partial_match: { label: "Partial Match", color: "bg-blue-50 text-blue-700 border-blue-200" },
  mismatch:      { label: "Mismatch",      color: "bg-red-50 text-red-700 border-red-200" },
  not_found:     { label: "Not Found",     color: "bg-orange-50 text-orange-700 border-orange-200" },
  pending:       { label: "Pending",       color: "bg-slate-100 text-slate-600 border-slate-200" },
};
const SEVERITY_CLR = { critical: "text-red-600 font-semibold", major: "text-orange-500", minor: "text-slate-400" };

function ReconciliationBadge({ status }) {
  if (!status) return null;
  const cfg = RECON_STATUS_CFG[status] || RECON_STATUS_CFG.pending;
  return <span className={`text-xs font-semibold px-1.5 py-0.5 rounded border ${cfg.color}`}>{cfg.label}</span>;
}

function ExternalReconcilePanel({ entityType, entityId, onSaved }) {
  const [records,  setRecords]  = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [form, setForm] = useState({
    registry_type: "UNFCCC", registry_name: "", external_record_id: "",
    external_data: {}, notes: ""
  });
  const [extDataFields, setExtDataFields] = useState({ volume_tco2e: "", vintage_year: "", buyer_country: "", pathway: "", host_country: "" });

  const load = useCallback(async () => {
    try {
      const res = await client.get(`/cai/erl/records?entity_id=${entityId}`);
      setRecords(res.data.records || []);
    } catch {}
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.external_record_id.trim()) { toast.warning("External record ID required."); return; }
    // Build external_data from form fields
    const extData = {};
    Object.entries(extDataFields).forEach(([k, v]) => { if (v !== "") extData[k] = isNaN(v) ? v : Number(v); });
    if (Object.keys(extData).length === 0) { toast.warning("Enter at least one external data field for comparison."); return; }
    setSaving(true);
    try {
      const res = await client.post("/cai/erl/records", {
        entity_type: entityType, entity_id: entityId,
        registry_type: form.registry_type, registry_name: form.registry_name || null,
        external_record_id: form.external_record_id, registry_url: form.registry_url || null,
        external_data: extData, notes: form.notes || null,
      });
      const rec = res.data.reconciliation;
      const msg = `${res.data.record.record_id}: ${rec.status.toUpperCase()} — ${rec.matched_fields}/${rec.total_fields_checked} fields match`;
      if (rec.status === "mismatch") toast.error(msg);
      else if (rec.status === "matched") toast.success(msg);
      else toast.info(msg);
      setShowForm(false);
      load();
      if (onSaved) onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const latestRecord = records[0];

  return (
    <div className="mt-2 space-y-2" data-testid={`recon-panel-${entityId}`}>
      {/* Latest reconciliation status */}
      {latestRecord && (
        <div className={`rounded-lg px-3 py-2 border text-xs ${
          latestRecord.reconciliation_status === "mismatch" ? "bg-red-50 border-red-200" :
          latestRecord.reconciliation_status === "matched" ? "bg-emerald-50 border-emerald-200" :
          "bg-slate-50 border-slate-200"
        }`}>
          <div className="flex items-center gap-2 mb-1">
            <ReconciliationBadge status={latestRecord.reconciliation_status} />
            <span className="text-slate-500">{latestRecord.registry_type} / {latestRecord.external_record_id}</span>
            <span className="text-xs text-slate-400 ml-auto">{new Date(latestRecord.reconciled_at || latestRecord.created_at).toLocaleDateString()}</span>
          </div>
          <p className="text-xs text-slate-500">{latestRecord.matched_fields}/{latestRecord.total_fields_checked} fields match</p>
          {latestRecord.discrepancies?.length > 0 && (
            <div className="mt-1 space-y-0.5">
              {latestRecord.discrepancies.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`text-xs ${SEVERITY_CLR[d.severity] || SEVERITY_CLR.minor}`}>{d.field}</span>
                  <span className="text-xs text-red-600">int: {String(d.internal_value)}</span>
                  <span className="text-xs text-slate-400">vs</span>
                  <span className="text-xs text-emerald-600">ext: {String(d.external_value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reconcile button */}
      {!showForm && (
        <button onClick={() => setShowForm(true)} data-testid={`open-recon-form-${entityId}`}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold border border-blue-200 rounded-lg px-2 py-0.5 bg-blue-50">
          <Globe size={9} /> {records.length === 0 ? "Add External Record" : "Update Record"}
        </button>
      )}

      {/* Reconcile form */}
      {showForm && (
        <div className="border border-blue-200 rounded-xl p-3 bg-blue-50/30 space-y-2" data-testid={`recon-form-${entityId}`}>
          <p className="text-xs font-semibold text-blue-700">External Registry Data</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-400 mb-0.5 block">Registry Type</label>
              <select value={form.registry_type} onChange={e => setForm(f=>({...f,registry_type:e.target.value}))}
                className="w-full h-8 px-2 text-xs border border-slate-200 rounded bg-white focus:outline-none"
                data-testid="recon-registry-type">
                {["UNFCCC","HostRegistry","BuyerRegistry","Platform"].map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-0.5 block">External Record ID *</label>
              <input value={form.external_record_id} onChange={e => setForm(f=>({...f,external_record_id:e.target.value}))}
                placeholder="UNFCCC-ART6-2026-001"
                className="w-full h-8 px-2 text-xs border border-slate-200 rounded focus:outline-none"
                data-testid="recon-ext-record-id" />
            </div>
          </div>
          <p className="text-xs text-slate-400 font-semibold uppercase mt-1">External Data (what the registry says)</p>
          <div className="grid grid-cols-3 gap-1.5">
            {[
              ["volume_tco2e","Volume (tCO₂e)","50.0"],
              ["vintage_year","Vintage Year","2023"],
              ["buyer_country","Buyer Country","CH"],
              ["pathway","Pathway","art6_2"],
              ["host_country","Host Country","ZA"],
            ].map(([k,l,p]) => (
              <div key={k}>
                <label className="text-xs text-slate-400 mb-0.5 block">{l}</label>
                <input value={extDataFields[k] || ""} onChange={e => setExtDataFields(f=>({...f,[k]:e.target.value}))}
                  placeholder={p} className="w-full h-7 px-2 text-xs border border-slate-200 rounded focus:outline-none"
                  data-testid={`recon-ext-${k}`} />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-1">
            <button onClick={handleSubmit} disabled={saving}
              className="flex-1 h-8 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 disabled:opacity-50"
              data-testid="recon-submit-btn">
              {saving ? "Reconciling..." : "Submit & Reconcile"}
            </button>
            <button onClick={() => setShowForm(false)} className="px-3 h-8 text-slate-500 text-xs border border-slate-200 rounded hover:bg-slate-50">Cancel</button>
          </div>
          <p className="text-xs text-slate-400 italic">Fields left blank are excluded from comparison. Only enter fields visible in the external registry.</p>
        </div>
      )}
    </div>
  );
}


// ── ERL Status Badge ─────────────────────────────────────────────────────
const ERL_STATUS_CFG = {
  not_linked:  { label: "Not Linked",  color: "bg-slate-100 text-slate-500 border-slate-200" },
  pending:     { label: "Pending",     color: "bg-blue-50 text-blue-600 border-blue-200" },
  matched:     { label: "Matched",     color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  mismatched:  { label: "Mismatch!",   color: "bg-red-50 text-red-700 border-red-200" },
};

function ERLBadge({ erl, compact = false, onViewEMS = null }) {
  if (!erl) return null;
  const status = erl.erl_status || "not_linked";
  const cfg    = ERL_STATUS_CFG[status] || ERL_STATUS_CFG.not_linked;
  if (compact && status === "not_linked") return null;
  const isMismatch = status === "mismatched";
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded border cursor-default ${cfg.color} ${isMismatch ? "animate-pulse" : ""}`}
      title={erl.erl_ref_id ? `${erl.erl_type} / ${erl.erl_ref_id}` : "No external reference"}>
      <Globe size={8} />
      {compact
        ? (erl.erl_ref_id ? `ERL: ${erl.erl_ref_id.slice(-8)}` : "ERL: —")
        : `${cfg.label}${erl.erl_ref_id ? ` · ${erl.erl_ref_id}` : ""}`}
      {isMismatch && onViewEMS && (
        <button onClick={e => { e.stopPropagation(); onViewEMS(); }}
          className="ml-1 underline text-xs" data-testid="erl-view-ems-link">
          See EMS
        </button>
      )}
    </span>
  );
}

function ERLLinkForm({ entityType, entityId, currentErl, onSave, onClose }) {
  const [form, setForm] = useState({
    erl_type:   currentErl?.erl_type   || "UNFCCC",
    erl_ref_id: currentErl?.erl_ref_id || "",
    erl_status: currentErl?.erl_status || "pending",
    erl_notes:  currentErl?.erl_notes  || "",
  });
  const [saving, setSaving] = useState(false);
  const ERL_TYPES    = ["UNFCCC", "HostRegistry", "BuyerRegistry", "Platform"];
  const ERL_STATUSES = ["pending", "matched", "mismatched"];
  const urlMap = { itmo: `/cai/erl/itmo/${entityId}`, ca: `/cai/erl/ca/${entityId}`, submission: `/cai/erl/submission/${entityId}` };

  const handleSave = async () => {
    if (!form.erl_ref_id.trim()) { toast.warning("External reference ID required."); return; }
    setSaving(true);
    try {
      await client.patch(urlMap[entityType], form);
      toast.success(`External reference linked: ${form.erl_type} / ${form.erl_ref_id}`);
      onSave(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Link failed"); }
    setSaving(false);
  };

  return (
    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50 space-y-2 mt-2" data-testid="erl-link-form">
      <p className="text-xs font-semibold text-slate-600">Link External Reference</p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-slate-400 mb-0.5 block">Registry Type</label>
          <select value={form.erl_type} onChange={e => setForm(f=>({...f,erl_type:e.target.value}))}
            className="w-full h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
            data-testid="erl-type-select">
            {ERL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-0.5 block">Status</label>
          <select value={form.erl_status} onChange={e => setForm(f=>({...f,erl_status:e.target.value}))}
            className="w-full h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
            data-testid="erl-status-select">
            {ERL_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs text-slate-400 mb-0.5 block">External Reference ID *</label>
        <input value={form.erl_ref_id} onChange={e => setForm(f=>({...f,erl_ref_id:e.target.value}))}
          placeholder="e.g. UNFCCC-BTR-2026-NG-001"
          className="w-full h-8 px-2 text-xs border border-slate-200 rounded-lg focus:outline-none"
          data-testid="erl-ref-id-input" />
      </div>
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving}
          className="flex-1 h-8 bg-[#059669] text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          data-testid="erl-save-btn">
          {saving ? "Saving..." : "Link Reference"}
        </button>
        <button onClick={onClose} className="px-3 h-8 border border-slate-200 text-slate-500 text-xs rounded-lg hover:bg-slate-50">Cancel</button>
      </div>
      <p className="text-xs text-slate-400 italic">
        Manual entry only — no live API connection to UNFCCC or host country registries yet. Foundation for future integration.
      </p>
    </div>
  );
}


function FlagCard({ flag, onAction }) {
  const [expanded, setExpanded] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [form, setForm] = useState({ resolution_notes: "", supporting_documents: [] });
  const [dismissForm, setDismissForm] = useState({ dismissed_reason: "" });
  const [showResolve, setShowResolve] = useState(false);
  const [showDismiss, setShowDismiss] = useState(false);

  const isActive   = ["open", "under_investigation"].includes(flag.status);
  const isBlocking = isActive && ["critical", "high"].includes(flag.severity);
  const sevCfg     = SEV_CFG[flag.severity] || SEV_CFG.medium;

  const handleInvestigate = async () => {
    setLoading(true);
    try {
      await client.patch(`/cai/ems/flags/${flag.flag_id}/status`, { status: "under_investigation", notes: "Marked for investigation." });
      toast.success(`${flag.flag_id} marked as under investigation`);
      onAction();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  const handleResolve = async () => {
    if (!form.resolution_notes || form.resolution_notes.trim().length < 10) {
      toast.warning("Resolution notes must be at least 10 characters.");
      return;
    }
    setLoading(true);
    try {
      const res = await client.post(`/cai/ems/flags/${flag.flag_id}/resolve`, form);
      toast.success(res.data.message);
      setShowResolve(false);
      onAction();
    } catch (err) { toast.error(err.response?.data?.detail || "Resolution failed"); }
    setLoading(false);
  };

  const handleVerify = async () => {
    setLoading(true);
    try {
      await client.post(`/cai/ems/flags/${flag.flag_id}/verify`);
      toast.success(`${flag.flag_id} verified and closed`);
      onAction();
    } catch (err) { toast.error(err.response?.data?.detail || "Verification failed"); }
    setLoading(false);
  };

  const handleDismiss = async () => {
    if (!dismissForm.dismissed_reason || dismissForm.dismissed_reason.trim().length < 10) {
      toast.warning("Dismiss reason must be at least 10 characters.");
      return;
    }
    setLoading(true);
    try {
      await client.post(`/cai/ems/flags/${flag.flag_id}/dismiss`, dismissForm);
      toast.success(`${flag.flag_id} dismissed`);
      setShowDismiss(false);
      onAction();
    } catch (err) { toast.error(err.response?.data?.detail || "Dismiss failed"); }
    setLoading(false);
  };

  return (
    <div className={`bg-white rounded-xl border-l-4 overflow-hidden ${isBlocking ? sevCfg.ring : "border-slate-200"} border border-r border-t border-b`}
      style={{ borderLeftColor: flag.severity === "critical" ? "#DC2626" : flag.severity === "high" ? "#F97316" : flag.severity === "medium" ? "#F59E0B" : "#94A3B8" }}
      data-testid={`flag-card-${flag.flag_id}`}>
      {/* Header row */}
      <div className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50/50"
        onClick={() => setExpanded(e => !e)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-mono text-slate-400">{flag.flag_id}</span>
            <SeverityBadge severity={flag.severity} />
            <FlagStatusPill status={flag.status} />
            {isBlocking && (
              <span className="text-xs font-bold text-red-600 flex items-center gap-0.5">
                <Lock size={8}/> BLOCKING
              </span>
            )}
          </div>
          <p className="text-sm font-semibold text-slate-900">{flag.title}</p>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{flag.description}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-slate-400">{flag.entity_id}</span>
          {flag.detection_source === "passive_scan" && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200 font-semibold">auto</span>
          )}
          {expanded ? <ChevronDown size={13} className="text-slate-400" /> : <ChevronRight size={13} className="text-slate-400" />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-slate-100 px-4 pb-4 pt-3 space-y-3 bg-slate-50/30">
          {/* Details grid */}
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div><span className="text-slate-400">Type:</span> <strong className="text-slate-900 capitalize">{flag.flag_type?.replace(/_/g, " ")}</strong></div>
            <div><span className="text-slate-400">Entity:</span> <strong className="font-mono">{flag.entity_id}</strong></div>
            <div>
              <span className="text-slate-400">Detected by:</span>{" "}
              <strong className={
                flag.detection_source === "passive_scan" ? "text-blue-600" :
                flag.detection_source === "manual_admin" ? "text-indigo-600" :
                flag.detection_source === "erl_detection" ? "text-orange-600" : "text-slate-600"
              }>
                {flag.detection_source === "passive_scan" ? "Passive scan" :
                 flag.detection_source === "manual_admin" ? "Admin (manual)" :
                 flag.detection_source === "erl_detection" ? "ERL mismatch detection" : "Manual scan"}
              </strong>
            </div>
            <div><span className="text-slate-400">Trigger:</span> <strong className="font-mono text-xs">{flag.trigger_event || "—"}</strong></div>
            <div><span className="text-slate-400">Created:</span> <strong>{new Date(flag.created_at).toLocaleDateString()}</strong></div>
            {flag.audit_entry_count > 0 && <div><span className="text-slate-400">Log entries:</span> <strong>{flag.audit_entry_count}</strong></div>}
          </div>

          {/* ERL context block — shown for ERL-detected flags */}
          {flag.flag_type?.startsWith("erl_") && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl px-3 py-2.5" data-testid={`erl-context-${flag.flag_id}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Globe size={11} className="text-orange-500" />
                <p className="text-xs font-bold text-orange-700 uppercase tracking-wide">ERL Mismatch Context</p>
              </div>
              <p className="text-xs text-orange-700 mb-1">{flag.description}</p>
              <p className="text-xs text-orange-500 italic">
                This flag was automatically created by the External Reference Layer when the registry status changed to 'mismatched'.
                Resolve in EMS only after confirming alignment with the external registry.
              </p>
            </div>
          )}

          {/* Resolution eligibility hint — shown when ERL has been fixed but flag not yet resolved */}
          {flag.resolution_eligible && flag.status !== "resolved" && flag.status !== "verified" && (
            <div className="bg-teal-50 border border-teal-200 rounded-xl px-3 py-2.5" data-testid={`resolution-hint-${flag.flag_id}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <CheckCircle2 size={11} className="text-teal-600" />
                <p className="text-xs font-bold text-teal-700">Eligible for Resolution</p>
              </div>
              <p className="text-xs text-teal-600">{flag.resolution_hint}</p>
            </div>
          )}

          {/* Impact statement */}
          {flag.impact && (
            <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <p className="text-xs text-amber-600 font-semibold mb-0.5">RISK IMPACT</p>
              <p className="text-xs text-amber-700">{flag.impact}</p>
            </div>
          )}

          {/* Audit log snippet */}
          {flag.audit_entry_count > 0 && (
            <div>
              <p className="text-xs text-slate-400 font-semibold uppercase mb-1">
                <History size={9} className="inline mr-1" /> Audit Log ({flag.audit_entry_count} entries)
              </p>
            </div>
          )}

          {/* Resolution info if resolved */}
          {flag.resolution_notes && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              <p className="text-xs text-blue-600 font-semibold mb-0.5">RESOLUTION NOTES</p>
              <p className="text-xs text-blue-700">{flag.resolution_notes}</p>
              {flag.resolved_at && <p className="text-xs text-blue-400 mt-1">Resolved: {new Date(flag.resolved_at).toLocaleString()}</p>}
            </div>
          )}

          {/* Action buttons */}
          {isActive && (
            <div className="space-y-2">
              {/* Investigate */}
              {flag.status === "open" && (
                <button onClick={handleInvestigate} disabled={loading} data-testid={`investigate-btn-${flag.flag_id}`}
                  className="flex items-center gap-1.5 px-3 h-8 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50">
                  <Eye size={12} /> Mark as Under Investigation
                </button>
              )}

              {/* Resolve form */}
              {!showResolve && !showDismiss && (
                <div className="flex gap-2">
                  <button onClick={() => setShowResolve(true)} data-testid={`resolve-btn-${flag.flag_id}`}
                    className="flex items-center gap-1 px-3 h-8 bg-[#059669] text-white text-xs font-medium rounded-lg hover:bg-emerald-700">
                    <CheckCircle2 size={12} /> Resolve
                  </button>
                  <button onClick={() => setShowDismiss(true)} data-testid={`dismiss-btn-${flag.flag_id}`}
                    className="flex items-center gap-1 px-3 h-8 border border-slate-200 text-slate-600 text-xs rounded-lg hover:bg-slate-100">
                    <XCircle size={12} /> Dismiss
                  </button>
                </div>
              )}

              {/* Resolution form */}
              {showResolve && (
                <div className="border border-emerald-200 bg-emerald-50/40 rounded-xl p-3 space-y-2">
                  <p className="text-xs font-semibold text-emerald-700">Resolution Details — Required</p>
                  <textarea
                    value={form.resolution_notes}
                    onChange={e => setForm(f => ({ ...f, resolution_notes: e.target.value }))}
                    placeholder="Describe how this issue was resolved. Minimum 10 characters. Include what data changed, who was involved, and what evidence supports the resolution."
                    rows={3}
                    className="w-full px-3 py-2 text-xs border border-emerald-200 rounded-lg focus:outline-none bg-white resize-none"
                    data-testid={`resolution-notes-${flag.flag_id}`} />
                  {flag.verification_required && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle size={9} /> This flag requires admin verification after resolution.
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button onClick={handleResolve} disabled={loading}
                      className="flex-1 h-8 bg-[#059669] text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                      {loading ? "Saving..." : "Submit Resolution"}
                    </button>
                    <button onClick={() => setShowResolve(false)} className="px-3 h-8 text-slate-500 text-xs rounded-lg hover:bg-slate-100">Cancel</button>
                  </div>
                </div>
              )}

              {/* Dismiss form */}
              {showDismiss && (
                <div className="border border-slate-200 bg-slate-50 rounded-xl p-3 space-y-2">
                  <p className="text-xs font-semibold text-slate-600">Dismiss Reason — Required (Admin Only)</p>
                  <textarea
                    value={dismissForm.dismissed_reason}
                    onChange={e => setDismissForm({ dismissed_reason: e.target.value })}
                    placeholder="Mandatory reason for dismissal. Dismissed flags remain in audit log and cannot be purged."
                    rows={2}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none bg-white resize-none"
                    data-testid={`dismiss-reason-${flag.flag_id}`} />
                  <div className="flex gap-2">
                    <button onClick={handleDismiss} disabled={loading}
                      className="flex-1 h-8 bg-slate-600 text-white text-xs font-medium rounded-lg hover:bg-slate-700 disabled:opacity-50">
                      {loading ? "Saving..." : "Confirm Dismiss"}
                    </button>
                    <button onClick={() => setShowDismiss(false)} className="px-3 h-8 text-slate-500 text-xs rounded-lg hover:bg-slate-100">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Verify button (resolved flags only) */}
          {flag.status === "resolved" && flag.verification_required && (
            <button onClick={handleVerify} disabled={loading} data-testid={`verify-btn-${flag.flag_id}`}
              className="flex items-center gap-1.5 px-3 h-8 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              <CheckCheck size={12} /> Verify Resolution (Admin)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ExceptionManagement() {
  const [summary,  setSummary]  = useState(null);
  const [flags,    setFlags]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filter,   setFilter]   = useState({ status: "", severity: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, flagRes] = await Promise.allSettled([
        client.get("/cai/ems/summary"),
        client.get("/cai/ems/flags"),
      ]);
      if (sumRes.status === "fulfilled") setSummary(sumRes.value.data);
      if (flagRes.status === "fulfilled") setFlags(flagRes.value.data.flags || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await client.post("/cai/ems/scan");
      toast.success(res.data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Scan failed"); }
    setScanning(false);
  };

  const filtered = flags.filter(f =>
    (!filter.status   || f.status   === filter.status) &&
    (!filter.severity || f.severity === filter.severity)
  );

  const criticalBlocking = flags.filter(f =>
    ["open", "under_investigation"].includes(f.status) && f.severity === "critical"
  );
  const highWarning = flags.filter(f =>
    ["open", "under_investigation"].includes(f.status) && f.severity === "high"
  );
  const activeBlocking = [...criticalBlocking, ...highWarning];

  return (
    <div className="space-y-5" data-testid="exception-management">
      {/* Risk Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: "Total Flags",    value: summary?.total_flags    ?? "—", color: "#0F172A" },
          { label: "Active",         value: summary?.active_flags   ?? "—", color: activeBlocking.length > 0 ? "#DC2626" : "#F59E0B" },
          { label: "Critical Open",  value: summary?.critical_active ?? "—", color: "#DC2626" },
          { label: "High Open",      value: summary?.high_active    ?? "—", color: "#F97316" },
          { label: "ITMO Blocked",   value: summary?.itmo_blocked ? "YES" : "No", color: summary?.itmo_blocked ? "#DC2626" : "#059669" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-slate-200 rounded-xl p-3 text-center" data-testid={`ems-kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
            <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
            <p className="text-xl font-bold mt-0.5" style={{ color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Blocking / Warning banners — separate critical from high */}
      {criticalBlocking.length > 0 && (
        <div className="border-2 border-red-400 bg-red-50 rounded-xl p-4" data-testid="ems-blocking-banner">
          <div className="flex items-start gap-3">
            <Lock size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-red-700">
                {criticalBlocking.length} critical flag(s) are hard-blocking ITMO creation
              </p>
              <p className="text-xs text-red-600 mt-0.5">
                ITMO creation is hard-gated until all critical flags are resolved and verified.
              </p>
              <div className="mt-2 space-y-1">
                {criticalBlocking.slice(0, 3).map(f => (
                  <p key={f.flag_id} className="text-xs text-red-600 flex items-center gap-1">
                    <X size={8} /> <strong>{f.flag_id}</strong>: {f.title}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {criticalBlocking.length === 0 && highWarning.length > 0 && (
        <div className="border border-orange-300 bg-orange-50 rounded-xl p-3" data-testid="ems-warning-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle size={15} className="text-orange-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-orange-700">
                {highWarning.length} high-severity flag(s) — BTR export warnings active
              </p>
              <p className="text-xs text-orange-600 mt-0.5">
                ITMO creation is not blocked, but BTR exports will include integrity warnings until these flags are resolved.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scan + filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={handleScan} disabled={scanning} data-testid="run-scan-btn"
          className="flex items-center gap-1.5 px-4 py-2 bg-[#0F172A] text-white text-xs font-semibold rounded-xl hover:bg-slate-700 disabled:opacity-50">
          <ScanLine size={13} className={scanning ? "animate-pulse" : ""} />
          {scanning ? "Scanning..." : "Run Manual Scan"}
        </button>
        <div className="text-xs text-slate-400">
          <p>Manual scan: explicit audit across all projects. Passive scan runs automatically on ITMO creation, CA updates, and LoA changes.</p>
        </div>
        {summary?.detection_sources && (
          <div className="ml-auto flex items-center gap-3 text-xs">
            {[
              { key: "passive_scan", label: "Passive", color: "text-blue-600" },
              { key: "manual_scan",  label: "Manual",  color: "text-slate-600" },
              { key: "manual_admin", label: "Admin",   color: "text-indigo-600" },
            ].map(({ key, label, color }) => (
              <span key={key} className={color}>
                {label}: <strong>{summary.detection_sources[key] || 0}</strong>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 ml-0">
          <select value={filter.severity} onChange={e => setFilter(f => ({ ...f, severity: e.target.value }))}
            className="h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
            data-testid="severity-filter">
            <option value="">All Severities</option>
            {["critical", "high", "medium", "low"].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
          </select>
          <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
            className="h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
            data-testid="status-filter">
            <option value="">All Statuses</option>
            {["open","under_investigation","resolved","verified","dismissed"].map(s => (
              <option key={s} value={s}>{EMS_STATUS_CFG[s]?.label || s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Flags list */}
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i=><div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <CheckCircle2 size={32} className="mx-auto text-emerald-400 mb-3" />
          <p className="text-sm font-semibold text-slate-600">
            {flags.length === 0 ? "No flags detected — run a scan to check integrity" : "No flags match your current filter"}
          </p>
          <p className="text-xs text-slate-400 mt-1">Run an integrity scan to detect issues across all sovereign transactions.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-slate-400">{filtered.length} flag(s) {filter.status || filter.severity ? "matching filters" : "total"}</p>
          {filtered.map(f => <FlagCard key={f.flag_id} flag={f} onAction={load} />)}
        </div>
      )}

      {/* EMS methodology note */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
        <Info size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-slate-900">Exception Management Methodology</p>
          <p className="text-xs text-slate-500 mt-0.5">
            All flags are persistent and immutable once created. Resolutions require notes of at least 10 characters.
            Critical and high-severity flags require admin verification after resolution.
            Dismissed flags remain in the audit log permanently and cannot be deleted.
            The immutable audit trail records every action: who performed it, when, and with what notes.
          </p>
        </div>
      </div>
    </div>
  );
}


export default function UNFCCCReportingPage() {
  const [portfolio,   setPortfolio]   = useState(null);
  const [emsSummary,  setEmsSummary]  = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [activeTab,   setActiveTab]   = useState("summary");
  const [selectedProject, setSelectedProject] = useState(null);

  const loadPortfolio = useCallback(async () => {
    setLoading(true);
    try {
      const [portRes, emsRes] = await Promise.allSettled([
        client.get("/cai/reporting/portfolio"),
        client.get("/cai/ems/summary"),
      ]);
      if (portRes.status === "fulfilled") setPortfolio(portRes.value.data);
      if (emsRes.status  === "fulfilled") setEmsSummary(emsRes.value.data);
    } catch (err) {
      toast.error("Failed to load reconciliation data");
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadPortfolio(); }, [loadPortfolio]);

  const handleSelectCA = (project) => {
    setSelectedProject(project);
    setActiveTab("ca-workflow");
  };

  return (
    <div className="p-6 lg:p-8 min-h-screen" style={{ backgroundColor: "#F8FAFC" }} data-testid="unfccc-reporting-page">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg bg-teal-600/15 flex items-center justify-center">
                <Globe size={14} className="text-teal-600" />
              </div>
              <PageHeader title="UNFCCC Reconciliation & Reporting" />
              <span className="text-xs px-2 py-0.5 rounded font-semibold bg-teal-50 text-teal-700 border border-teal-200">
                Article 6 Compliance
              </span>
            </div>
            <p className="text-xs text-slate-400">
              Sovereign ITMO audit layer — every transaction is traceable, reconcilable, and BTR-exportable.
              This is not a summary view; it is an exception detection and compliance workspace.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {portfolio && (
              <p className="text-xs text-slate-400">
                Updated {new Date(portfolio.generated_at).toLocaleTimeString()}
              </p>
            )}
            <button onClick={loadPortfolio} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 bg-white text-xs text-slate-600 rounded-xl hover:bg-slate-50"
              data-testid="refresh-btn">
              <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {/* Global integrity status bar — project + portfolio combined */}
        {(portfolio || emsSummary) && (
          <div className={`mt-4 rounded-xl border px-4 py-2.5 flex items-center gap-3 flex-wrap ${
            emsSummary?.critical_active > 0 || emsSummary?.portfolio_risk?.critical_portfolio > 0
              ? "bg-red-50 border-red-200"
              : emsSummary?.active_flags > 0
                ? "bg-amber-50 border-amber-200"
                : "bg-emerald-50 border-emerald-200"
          }`} data-testid="global-status-bar">
            {(emsSummary?.critical_active > 0 || emsSummary?.portfolio_risk?.critical_portfolio > 0)
              ? <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
              : emsSummary?.active_flags > 0
                ? <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />
                : <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />}
            <p className={`text-xs font-semibold ${
              emsSummary?.critical_active > 0 || emsSummary?.portfolio_risk?.critical_portfolio > 0
                ? "text-red-700"
                : emsSummary?.active_flags > 0 ? "text-amber-700" : "text-emerald-700"
            }`}>
              {emsSummary?.portfolio_risk?.critical_portfolio > 0
                ? `Critical portfolio risk — ${emsSummary.portfolio_risk.critical_portfolio} CPIE rule(s) violated. ITMO creation blocked.`
                : emsSummary?.critical_active > 0
                  ? `${emsSummary.critical_active} critical project flag(s) — ITMO creation blocked`
                  : `Integrity: ${portfolio?.total_projects ?? "—"} projects · ${portfolio?.portfolio_summary?.total_itmos_tco2e ?? "—"} tCO₂e ITMO volume`}
            </p>
            {emsSummary?.active_flags > 0 && emsSummary?.critical_active === 0 && emsSummary?.portfolio_risk?.critical_portfolio === 0 && (
              <span className="text-xs text-amber-600">({emsSummary.active_flags} active flag(s))</span>
            )}
            <div className="ml-auto flex items-center gap-2">
              {emsSummary?.portfolio_risk?.total_portfolio_active > 0 && (
                <button onClick={() => setActiveTab("portfolio-risk")}
                  className="flex items-center gap-1 text-xs font-semibold hover:opacity-80 border rounded px-2 py-0.5 border-orange-300 text-orange-600">
                  <Globe size={9}/> Portfolio Risk
                </button>
              )}
              {emsSummary?.itmo_blocked && (
                <button onClick={() => setActiveTab("exceptions")}
                  className="flex items-center gap-1 text-xs text-red-600 font-semibold hover:text-red-800 border border-red-200 rounded px-2 py-0.5">
                  <Lock size={9}/> Exceptions
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-transparent border-0 h-auto p-0 gap-0 border-b border-slate-200 w-full mb-6 overflow-x-auto">
          {[
            ["summary",        "Reconciliation Summary"],
            ["projects",       "Project Registry"],
            ["ca-workflow",    "CA Status Workflow"],
            ["portfolio-risk", emsSummary?.portfolio_risk?.total_portfolio_active > 0
              ? `Portfolio Risk (${emsSummary.portfolio_risk.total_portfolio_active})`
              : "Portfolio Risk"],
            ["bilateral",      "Bilateral Agreements"],
            ["exceptions",     emsSummary?.active_flags > 0 ? `Exceptions (${emsSummary.active_flags})` : "Exceptions"],
            ["audit-trail",    "Audit Trail"],
            ["export",         "Export"],
          ].map(([v, l]) => (
            <TabsTrigger key={v} value={v}
              data-testid={`tab-${v}`}
              className={`rounded-none border-b-2 border-transparent px-4 py-2.5 text-xs font-medium text-slate-500 data-[state=active]:border-teal-500 data-[state=active]:text-teal-600 data-[state=active]:bg-transparent whitespace-nowrap ${
                (v === "exceptions" && emsSummary?.critical_active > 0) ||
                (v === "portfolio-risk" && emsSummary?.portfolio_risk?.critical_portfolio > 0) ? "text-red-500" : ""
              }`}>
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="summary">
          {loading
            ? <div className="space-y-4"><div className="h-28 bg-slate-100 rounded-xl animate-pulse" /><div className="h-40 bg-slate-100 rounded-xl animate-pulse" /></div>
            : <ReconciliationSummary portfolio={portfolio} onRefresh={loadPortfolio} loading={loading} />}
        </TabsContent>

        <TabsContent value="projects">
          {loading
            ? <div className="space-y-2">{[1,2,3].map(i=><div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
            : <ProjectReconciliation projects={portfolio?.projects || []} onSelectCA={handleSelectCA} />}
        </TabsContent>

        <TabsContent value="ca-workflow">
          <CAStatusWorkflow projects={portfolio?.projects || []} preselected={selectedProject} />
        </TabsContent>

        <TabsContent value="portfolio-risk">
          <PortfolioRiskTab />
        </TabsContent>

        <TabsContent value="bilateral">
          <BilateralRegistry />
        </TabsContent>

        <TabsContent value="exceptions">
          <ExceptionManagement />
        </TabsContent>

        <TabsContent value="audit-trail">
          <AuditTrailTab />
        </TabsContent>

        <TabsContent value="export">
          <ExportLayer />
        </TabsContent>
      </Tabs>
    </div>
  );
}
